import React from 'react';
import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { ChevronDown, ChevronUp, Plus, Save, Trash2 } from 'lucide-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { YesNoDialog } from '@/components/ui/modules/yes-no-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { OdrLoadingSpinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { readMedcall2IntakeOptions, type Medcall2OptionRow } from '../medcall/medcall2IntakeOptions';
import type { Medcall2PersonAssignmentSubmission } from '../medcall/Medcall2PersonAssignmentForm';
import type { MedcallImlaiDueDateUpdate } from '../medcall/medcallImlaiDueDateWarning';
import { MedcallParticipantActions } from './MedcallWorkspaceParticipantActions';
import { MedcallAdherenceReportDialog, MedcallMedicationListDialog } from '../medcall/MedcallParticipantReports';
import { MedcallSendMessageDialog } from '../medcall/MedcallSendMessageDialog';
import { getMedcallStrengthOptionLabel, getMedcallStrengthOptionValue, resolveMedcallStoredStrength, resolveMedcallStrengthControlValue, resolveStoredMedicationExternalId, sortMedcallStrengthOptionsNumerically } from '../medcall/medcallMedicationCatalogSelection';
import { getMedcallStaffOptionLabel, orderMedcallStaffOptions } from '../medcall/medcallStaffOptions';
import type { MedcallStaffOption } from '../medcall/medcallStaffOptions';
import { buildMedcallProfileMutationVariables, getMedcallIdentityDialogCopy, getMedcallProfileFacilityProgram, normalizeMedcallProfileCidDraft, shouldShowMedcallAliasEntryFields } from '../medcall/medcallParticipantIdentityCompatibility';
import { didMedcall2TimelineMutationComplete, getVisibleMedcallTimelineRows, shouldShowMedcallMedicationHistory, userCanEditMedcallTimelineSite, userCanEditMedcallTimelineStatus, userCanEditMedcallTimelineWitness } from '../medcall/medcallTimelineCompatibility';
const MEDCALL2_PROFILE_OPTIONS = gql `
  query Medcall2ProfileOptions {
    medcallOptions: medcall2Options(names: ["programs", "facilities", "providers", "filter_domains"]) {
      option_name
      option_value_json
    }
  }
`;
const MEDCALL_PARTICIPANT_DETAIL_PAGE = gql `
  query MedcallParticipantDetailPage(
    $includeTimelines: Boolean
    $odrparticipant: Int
    $participantId: Int
    $preserveLegacyMedicationOrder: Boolean
  ) {
    medcallParticipantActionMeta {
      facilityOptions {
        label
        value
      }
      filterDomains
      programOptions {
        label
        value
      }
      providerOptions {
        label
        value
      }
      staffOptions {
        email
        fullname
        id
        username
      }
    }
    medcallParticipantDetail(
      includeTimelines: $includeTimelines
      odrparticipant: $odrparticipant
      participantId: $participantId
      preserveLegacyMedicationOrder: $preserveLegacyMedicationOrder
    ) {
      active
      activation_history {
        date
        deactivation_reason
        fullname
        status
        username
      }
      aliases {
        firstname
        lastname
      }
      cid
      created_at
      dob
      facility
      firstname
      history_ids {
        date
        fullname
        id
        username
      }
      id
      lastname
      medications {
        active
        days_of_week
        frequency
        id
        importance_level
        injection_info {
          administration_notes
          date_due
          date_given
          date_injection_arrived
          on_site
        }
        last_taken_date
        medicine_detail {
          drugbank_pcid
          external_id
          hits {
            value
          }
          name
          uuid
        }
        medicine_name
        prn
        psychotropic_meds
        refill_status
        refills {
          notify_week
          refill_date
        }
        route
        start_date
        stop_date
        stop_date_status
        strength
        timelines {
          date
          dose
          id
          injection_info {
            administration_notes
            date_due
            date_given
            date_injection_arrived
            on_site
          }
          med_id
          mparticipant_id
          notification_type_id
          refilled_at
          route
          site
          status
          strength
          taken_at
          time
          times_per_day
          witnessed_by_other
        }
        times_doses {
          dose
          time
        }
        times_per_day
        user_id
        uuid
      }
      notes {
        entryby
        entrydate
        id
        notes
      }
      nurse_name {
        email
        firstname
        lastname
        title
      }
      option_facility_id
      participant_id
      program
      project_sites_client_options {
        id
        week_start
      }
      project_sites_medication_statuses {
        date
        id
        notes
        status
      }
      provider
      psychiatrist {
        email
        firstname
        lastname
        title
      }
      staff_assigned
      updated_at
      user_id
      view_user_ids
    }
  }
`;
const MEDCALL_MEDICATION_TIMELINES = gql `
  query MedcallMedicationTimelines($medicationId: Int!) {
    medcallMedicationTimelines(medicationId: $medicationId) {
      date
      dose
      id
      injection_info {
        administration_notes
        date_due
        date_given
        date_injection_arrived
        on_site
      }
      med_id
      mparticipant_id
      notification_type_id
      refilled_at
      route
      site
      status
      strength
      taken_at
      time
      times_per_day
      witnessed_by_other
    }
  }
`;
const MEDCALL_COUNTY_HOLIDAYS = gql `
  query MedcallCountyHolidays {
    medcallCountyHolidays {
      date
      holiday
    }
  }
`;
const MEDCALL_ADD_TO_MY_CLIENTS = gql `
  mutation MedcallAddToMyClients($participantId: Int!) {
    medcallAddToMyClients(participantId: $participantId) {
      message
      responseCode
    }
  }
`;
const MEDCALL_REMOVE_FROM_MY_CLIENTS = gql `
  mutation MedcallRemoveFromMyClients($participantId: Int!) {
    medcallRemoveFromMyClients(participantId: $participantId) {
      message
      responseCode
    }
  }
`;
const MEDCALL_UPDATE_PARTICIPANT_PROFILE = gql `
  mutation MedcallUpdateParticipantProfile(
    $cid: String
    $dob: String
    $optionFacilityId: Int
    $participantId: Int!
    $preserveLegacySemantics: Boolean
    $program: String
    $provider: String
  ) {
    medcallUpdateParticipantProfile(
      cid: $cid
      dob: $dob
      optionFacilityId: $optionFacilityId
      participantId: $participantId
      preserveLegacySemantics: $preserveLegacySemantics
      program: $program
      provider: $provider
    ) {
      id
    }
  }
`;
const MEDCALL_UPDATE_PARTICIPANT_STAFF = gql `
  mutation MedcallUpdateParticipantStaff($newStaffId: Int!, $participantId: Int!) {
    medcallUpdateParticipantStaff(newStaffId: $newStaffId, participantId: $participantId) {
      id
    }
  }
`;
const MEDCALL_UPDATE_PARTICIPANT_NURSE = gql `
  mutation MedcallUpdateParticipantNurse(
    $email: String!
    $firstname: String!
    $lastname: String!
    $participantId: Int!
    $title: String!
  ) {
    medcallUpdateParticipantNurse(
      email: $email
      firstname: $firstname
      lastname: $lastname
      participantId: $participantId
      title: $title
    ) {
      id
    }
  }
`;
const MEDCALL_UPDATE_PARTICIPANT_PSYCHIATRIST = gql `
  mutation MedcallUpdateParticipantPsychiatrist(
    $email: String
    $firstname: String!
    $lastname: String!
    $participantId: Int!
    $title: String!
  ) {
    medcallUpdateParticipantPsychiatrist(
      email: $email
      firstname: $firstname
      lastname: $lastname
      participantId: $participantId
      title: $title
    ) {
      id
    }
  }
`;
const MEDCALL_ADD_PARTICIPANT_NOTE = gql `
  mutation MedcallAddParticipantNote($note: String!, $participantId: Int!) {
    medcallAddParticipantNote(note: $note, participantId: $participantId) {
      id
    }
  }
`;
const MEDCALL_ADD_PARTICIPANT_ALIAS = gql `
  mutation MedcallAddParticipantAlias($firstname: String!, $lastname: String!, $participantId: Int!) {
    medcallAddParticipantAlias(firstname: $firstname, lastname: $lastname, participantId: $participantId) {
      id
    }
  }
`;
const MEDCALL_DELETE_PARTICIPANT_NOTE = gql `
  mutation MedcallDeleteParticipantNote($noteId: Int!, $participantId: Int!) {
    medcallDeleteParticipantNote(noteId: $noteId, participantId: $participantId) {
      message
      responseCode
      responseID
    }
  }
`;
const MEDCALL_SET_PARTICIPANT_ACTIVE = gql `
  mutation MedcallSetParticipantActive(
    $activationBackDate: String
    $active: Boolean!
    $deactivationReason: String
    $participantId: Int!
  ) {
    medcallSetParticipantActive(
      activationBackDate: $activationBackDate
      active: $active
      deactivationReason: $deactivationReason
      participantId: $participantId
    ) {
      id
    }
  }
`;
const MEDCALL_CREATE_MEDICATION = gql `
  mutation MedcallCreateMedication(
    $administrationNotes: String
    $daysOfWeek: [String]
    $frequency: String!
    $importanceLevel: Int
    $injectionDateArrived: String
    $injectionDateDue: String
    $injectionDateGiven: String
    $injectionOnSite: Int
    $medicineDetail: String
    $medicineName: String!
    $participantId: Int!
    $pharmabankUuid: String
    $prn: Boolean
    $psychotropicMeds: Boolean
    $refillDate: String
    $refillStatus: Boolean
    $route: String!
    $startDate: String
    $stopDate: String
    $stopDateStatus: Boolean
    $strength: String!
    $timesPerDay: String!
  ) {
    medcallCreateMedication(
      administrationNotes: $administrationNotes
      daysOfWeek: $daysOfWeek
      frequency: $frequency
      importanceLevel: $importanceLevel
      injectionDateArrived: $injectionDateArrived
      injectionDateDue: $injectionDateDue
      injectionDateGiven: $injectionDateGiven
      injectionOnSite: $injectionOnSite
      medicineDetail: $medicineDetail
      medicineName: $medicineName
      participantId: $participantId
      pharmabankUuid: $pharmabankUuid
      prn: $prn
      psychotropicMeds: $psychotropicMeds
      refillDate: $refillDate
      refillStatus: $refillStatus
      route: $route
      startDate: $startDate
      stopDate: $stopDate
      stopDateStatus: $stopDateStatus
      strength: $strength
      timesPerDay: $timesPerDay
    ) {
      id
    }
  }
`;
const MEDCALL_UPDATE_MEDICATION = gql `
  mutation MedcallUpdateMedication(
    $administrationNotes: String
    $daysOfWeek: [String]
    $frequency: String!
    $importanceLevel: Int
    $injectionDateArrived: String
    $injectionDateDue: String
    $injectionDateGiven: String
    $injectionOnSite: Int
    $medicationId: Int!
    $medicineDetail: String
    $medicineName: String!
    $pharmabankUuid: String
    $prn: Boolean
    $psychotropicMeds: Boolean
    $refillDate: String
    $refillStatus: Boolean
    $route: String!
    $startDate: String
    $stopDate: String
    $stopDateStatus: Boolean
    $strength: String!
    $timesPerDay: String!
  ) {
    medcallUpdateMedication(
      administrationNotes: $administrationNotes
      daysOfWeek: $daysOfWeek
      frequency: $frequency
      importanceLevel: $importanceLevel
      injectionDateArrived: $injectionDateArrived
      injectionDateDue: $injectionDateDue
      injectionDateGiven: $injectionDateGiven
      injectionOnSite: $injectionOnSite
      medicationId: $medicationId
      medicineDetail: $medicineDetail
      medicineName: $medicineName
      pharmabankUuid: $pharmabankUuid
      prn: $prn
      psychotropicMeds: $psychotropicMeds
      refillDate: $refillDate
      refillStatus: $refillStatus
      route: $route
      startDate: $startDate
      stopDate: $stopDate
      stopDateStatus: $stopDateStatus
      strength: $strength
      timesPerDay: $timesPerDay
    ) {
      id
    }
  }
`;
const MEDCALL_UPDATE_IMLAI_DUE_DATE = gql `
  mutation MedcallUpdateImlaiDueDate($dueDate: String!, $medicationId: Int!) {
    medcallUpdateImlaiDueDate(dueDate: $dueDate, medicationId: $medicationId) {
      id
      injection_info {
        date_due
      }
    }
  }
`;
const MEDCALL_SEARCH_MEDICATIONS = gql `
  query MedcallSearchMedications($searchText: String!) {
    medcallSearchMedications(searchText: $searchText) {
      displayName
      drugbankPcid
      externalId
      hits {
        value
      }
      isCustom
      level
      medicineDetail
      name
      source
      uuid
    }
  }
`;
const MEDCALL_MEDICATION_STRENGTHS = gql `
  query MedcallMedicationStrengths($externalId: String!, $medicineName: String) {
    medcallMedicationStrengths(externalId: $externalId, medicineName: $medicineName) {
      displayName
      externalId
      isCustom
      name
      strength
    }
  }
`;
const MEDCALL_SET_MEDICATION_ACTIVE = gql `
  mutation MedcallSetMedicationActive($active: Boolean!, $medicationId: Int!) {
    medcallSetMedicationActive(active: $active, medicationId: $medicationId) {
      id
    }
  }
`;
const MEDCALL_DELETE_MEDICATION = gql `
  mutation MedcallDeleteMedication($medicationId: Int!) {
    medcallDeleteMedication(medicationId: $medicationId) {
      message
      responseCode
    }
  }
`;
const MEDCALL_UPDATE_MEDICATION_TIMELINE = gql `
  mutation MedcallUpdateMedicationTimeline(
    $preserveLegacySemantics: Boolean
    $status: String
    $time: String
    $timelineId: Int!
    $witnessedByOther: Boolean
  ) {
    medcallUpdateMedicationTimeline(
      preserveLegacySemantics: $preserveLegacySemantics
      status: $status
      time: $time
      timelineId: $timelineId
      witnessedByOther: $witnessedByOther
    ) {
      id
    }
  }
`;
const MEDCALL_UPDATE_MEDICATION_TIMELINE_SITE = gql `
  mutation MedcallUpdateMedicationTimelineSite(
    $preserveLegacySemantics: Boolean
    $site: String
    $timelineId: Int!
  ) {
    medcallUpdateMedicationTimelineSite(
      preserveLegacySemantics: $preserveLegacySemantics
      site: $site
      timelineId: $timelineId
    ) {
      id
    }
  }
`;
const MEDCALL_UPDATE_MEDICATION_ADMINISTRATION_NOTES = gql `
  mutation MedcallUpdateMedicationAdministrationNotes(
    $administrationNotes: String!
    $preserveLegacySemantics: Boolean
    $timelineId: Int!
  ) {
    medcallUpdateMedicationAdministrationNotes(
      administrationNotes: $administrationNotes
      preserveLegacySemantics: $preserveLegacySemantics
      timelineId: $timelineId
    ) {
      id
      injection_info {
        administration_notes
      }
    }
  }
`;
const MEDCALL_UPDATE_PROJECT_SITES_MEDICATION_STATUSES = gql `
  mutation MedcallUpdateProjectSitesMedicationStatuses(
    $endDate: String
    $notes: String
    $participantId: Int!
    $startDate: String!
    $status: String!
  ) {
    medcallUpdateProjectSitesMedicationStatuses(
      endDate: $endDate
      notes: $notes
      participantId: $participantId
      startDate: $startDate
      status: $status
    ) {
      id
    }
  }
`;
const MEDCALL_UPDATE_PROJECT_SITES_CLIENT_OPTIONS = gql `
  mutation MedcallUpdateProjectSitesClientOptions($participantId: Int!, $weekStart: Int!) {
    medcallUpdateProjectSitesClientOptions(participantId: $participantId, weekStart: $weekStart) {
      id
    }
  }
`;
const ACTION_LINK_GROUP_CLASS = 'flex flex-wrap items-center gap-2 [&_button]:h-8';
const SELECT_EMPTY_VALUE = '__empty__';
const NURSELESS_PROGRAMS = ['ODRH Scattered/ERC', 'FIST ERC', 'DSH ERC'];
const PROGRAM_LEVEL_MANAGEMENT_PROGRAMS = [...NURSELESS_PROGRAMS, 'Project Sites'];
const NURSE_TITLES = ['LVN', 'MA', 'CNA', 'RN', 'LPT', 'NP', 'EMT'];
const PSYCHIATRIST_TITLES = ['MD', 'NP', 'PA'];
const DEACTIVATION_OPTIONS = ['AWOL', 'Hospitalization/Treatment Facility', 'Re-arrest', 'Other'];
const MEDICATION_ROUTE_OPTIONS = ['AU', 'AS', 'AD', 'PO', 'IM-LAI', 'IM-Other', 'Subq', 'Rectally', 'IV', 'OD', 'OS', 'OU', 'INH', 'SUPP', 'Nasal', 'SL', 'TD', 'TOP', 'VAG', 'SUBQ LAI'];
const MEDICATION_TIMELINE_SITE_OPTIONS = [
    { label: 'Not Set', value: '' },
    { label: 'Left Deltoid', value: 'left-deltoid' },
    { label: 'Right Deltoid', value: 'right-deltoid' },
    { label: 'Left Gluteal', value: 'left-gluteal' },
    { label: 'Right Gluteal', value: 'right-gluteal' },
];
const MEDICATION_DAY_OPTIONS = [
    { label: 'Mon', value: 'monday' },
    { label: 'Tue', value: 'tuesday' },
    { label: 'Wed', value: 'wednesday' },
    { label: 'Thu', value: 'thursday' },
    { label: 'Fri', value: 'friday' },
    { label: 'Sat', value: 'saturday' },
    { label: 'Sun', value: 'sunday' },
];
const MEDICATION_TIME_OPTION_LABELS: Record<string, string> = {
    bid: 'BID',
    'q15min-int': 'Q15MIN-INT',
    q2h: 'Q2H',
    q4h: 'Q4H',
    q6h: 'Q6H',
    q8h: 'Q8H',
    qam: 'QAM',
    qday: 'QDAY',
    qhs: 'QHS',
    qid: 'QID',
    qod: 'QOD',
    tid: 'TID',
};
const PROJECT_SITES_TRACKER_VIEW_OPTIONS = [
    { label: 'Day', value: 'day' },
    { label: 'Week', value: 'week' },
    { label: 'Month', value: 'month' },
] as const;
const PROJECT_SITES_TRACKER_STATUS_OPTIONS = [
    { label: 'Taken', value: 'taken' },
    { label: 'Not Taken', value: 'notTaken' },
];
const PROJECT_SITES_WEEK_START_OPTIONS = [
    { label: 'Sunday - Saturday', value: 0 },
    { label: 'Monday - Sunday', value: 1 },
    { label: 'Tuesday - Monday', value: 2 },
    { label: 'Wednesday - Tuesday', value: 3 },
    { label: 'Thursday - Wednesday', value: 4 },
    { label: 'Friday - Thursday', value: 5 },
    { label: 'Saturday - Friday', value: 6 },
];
const MEDICATION_FREQUENCY_OPTIONS = [
    { label: 'Every Day', scheduleOptions: ['qday', 'qam', 'qhs', 'bid', 'tid', 'qid', 'q2h', 'q4h', 'q6h', 'q8h'], value: 'everyday' },
    { label: 'Every Other Day', scheduleOptions: ['qod'], value: 'everyotherday' },
    { label: 'Once', scheduleOptions: ['qday'], value: 'once' },
    { label: 'Q15MIN-INT', scheduleOptions: ['q15min-int'], value: 'q15min-int' },
    { label: 'QWK', scheduleOptions: ['qday'], value: 'qwk' },
    { label: 'Q2WK', scheduleOptions: ['qday'], value: 'q2wk' },
    { label: 'Q3WK', scheduleOptions: ['qday'], value: 'q3wk' },
    { label: 'Q4WK', scheduleOptions: ['qday'], value: 'q4wk' },
    { label: 'Q5WK', scheduleOptions: ['qday'], value: 'q5wk' },
    { label: 'Q6WK', scheduleOptions: ['qday'], value: 'q6wk' },
    { label: 'Q7WK', scheduleOptions: ['qday'], value: 'q7wk' },
    { label: 'Q8WK', scheduleOptions: ['qday'], value: 'q8wk' },
    { label: 'Q10WK', scheduleOptions: ['qday'], value: 'q10wk' },
    { label: 'Q2MONTH', scheduleOptions: ['qday'], value: 'q2month' },
    { label: 'Q3MONTH', scheduleOptions: ['qday'], value: 'q3month' },
    { label: 'Q4MONTH', scheduleOptions: ['qday'], value: 'q4month' },
    { label: 'Q6MONTH', scheduleOptions: ['qday'], value: 'q6month' },
    { label: 'Custom Week Days', scheduleOptions: ['qday', 'qam', 'qhs', 'bid', 'tid', 'qid', 'q2h', 'q4h', 'q6h', 'q8h'], value: 'custom_days' },
];
const IM_LAI_FILTERED_FREQUENCIES = new Set(['everyday', 'custom_days']);
export type MedcallBootstrapContext = {
    defaultLandingPath?: string | null;
    hasMedcallPowerAccess?: boolean;
    hasSuperAccess?: boolean;
    isAdminUser?: boolean;
    medcallUserId: number;
};
type MedcallPerson = {
    email?: string | null;
    firstname?: string | null;
    lastname?: string | null;
    title?: string | null;
};
type MedcallAlias = {
    firstname?: string | null;
    lastname?: string | null;
};
type MedcallActivationHistoryItem = {
    date?: string | null;
    deactivation_reason?: string | null;
    fullname?: string | null;
    status?: string | null;
    username?: string | null;
};
type MedcallStaffHistoryItem = {
    date?: string | null;
    fullname?: string | null;
    id?: number | null;
    username?: string | null;
};
type MedcallMedicationRefill = {
    notify_week?: string | null;
    refill_date?: string | null;
};
type MedcallMedicationDetail = {
    drugbank_pcid?: string | null;
    external_id?: string | null;
    hits?: Array<{
        value?: string | null;
    }> | null;
    name?: string | null;
    uuid?: string | null;
};
type MedcallMedicationSearchItem = {
    displayName?: string | null;
    drugbankPcid?: string | null;
    externalId?: string | null;
    hits?: Array<{
        value?: string | null;
    }> | null;
    isCustom?: boolean | null;
    level?: number | null;
    medicineDetail?: string | null;
    name?: string | null;
    source?: string | null;
    uuid?: string | null;
};
type MedcallMedicationStrengthOption = {
    displayName?: string | null;
    externalId?: string | null;
    isCustom?: boolean | null;
    name?: string | null;
    strength?: string | null;
};
type MedcallMedicationSearchData = {
    medcallSearchMedications?: MedcallMedicationSearchItem[] | null;
};
type MedcallMedicationStrengthsData = {
    medcallMedicationStrengths?: MedcallMedicationStrengthOption[] | null;
};
type MedcallMedicationTimesDose = {
    dose?: string | null;
    time?: string | null;
};
type MedcallMedicationInjectionInfo = {
    administration_notes?: string | null;
    date_due?: string | null;
    date_given?: string | null;
    date_injection_arrived?: string | null;
    on_site?: number | null;
};
type MedcallMedicationTimeline = {
    date?: string | null;
    dose?: string | null;
    id: number;
    injection_info?: MedcallMedicationInjectionInfo[] | null;
    med_id?: number | null;
    mparticipant_id?: number | null;
    notification_type_id?: number | null;
    refilled_at?: string | null;
    route?: string | null;
    site?: string | null;
    status?: string | null;
    strength?: string | null;
    taken_at?: string | null;
    time?: string | null;
    times_per_day?: string | null;
    witnessed_by_other?: boolean | null;
};
type MedcallParticipantMedication = {
    active?: boolean | null;
    days_of_week?: string[] | null;
    frequency?: string | null;
    id: number;
    importance_level?: number | null;
    injection_info?: MedcallMedicationInjectionInfo[] | null;
    last_taken_date?: string | null;
    medicine_detail?: MedcallMedicationDetail[] | null;
    medicine_name?: string | null;
    prn?: boolean | null;
    psychotropic_meds?: boolean | null;
    refill_status?: boolean | null;
    refills?: MedcallMedicationRefill[] | null;
    route?: string | null;
    start_date?: string | null;
    stop_date?: string | null;
    stop_date_status?: string | null;
    strength?: string | null;
    timelines?: MedcallMedicationTimeline[] | null;
    times_doses?: MedcallMedicationTimesDose[] | null;
    times_per_day?: string | null;
    user_id?: number | null;
    uuid?: string | null;
};
type MedcallParticipantNote = {
    entryby?: string | null;
    entrydate?: string | null;
    id: number;
    notes?: string | null;
};
type MedcallProjectSitesMedicationStatus = {
    date?: string | null;
    id: number;
    notes?: string | null;
    status?: string | null;
};
type MedcallProjectSitesClientOptions = {
    id: number;
    week_start?: number | null;
};
type MedcallListOption = {
    label?: string | null;
    value?: string | null;
};
type MedcallParticipantDetail = {
    active?: boolean | null;
    activation_history?: MedcallActivationHistoryItem[] | null;
    aliases?: MedcallAlias[] | null;
    cid?: string | null;
    created_at?: string | null;
    dob?: string | null;
    facility?: string | null;
    firstname?: string | null;
    history_ids?: MedcallStaffHistoryItem[] | null;
    id: number;
    lastname?: string | null;
    medications?: MedcallParticipantMedication[] | null;
    notes?: MedcallParticipantNote[] | null;
    nurse_name?: MedcallPerson | null;
    option_facility_id?: number | null;
    participant_id?: number | null;
    program?: string | null;
    project_sites_client_options?: MedcallProjectSitesClientOptions | null;
    project_sites_medication_statuses?: MedcallProjectSitesMedicationStatus[] | null;
    provider?: string | null;
    psychiatrist?: MedcallPerson | null;
    staff_assigned?: string | null;
    updated_at?: string | null;
    user_id?: number | null;
    view_user_ids?: string[] | null;
};
type MedcallParticipantActionMeta = {
    facilityOptions?: MedcallListOption[] | null;
    filterDomains?: string[] | null;
    programOptions?: MedcallListOption[] | null;
    providerOptions?: MedcallListOption[] | null;
    staffOptions?: MedcallStaffOption[] | null;
};
type MedcallParticipantDetailData = {
    medcallParticipantActionMeta: MedcallParticipantActionMeta | null;
    medcallParticipantDetail: MedcallParticipantDetail | null;
};
type MedcallCountyHolidaysData = {
    medcallCountyHolidays?: Array<{
        date?: string | null;
        holiday?: string | null;
    } | null> | null;
};
type RouteLocationState = {
    from?: string;
    openDialog?: 'medication' | 'message';
};
type MedcallMedicationView = 'active' | 'all' | 'historical' | 'prn';
type ProjectSitesTrackerView = 'day' | 'month' | 'week';
type ProjectSitesTrackerSelection = {
    endDate: string | null;
    label: string;
    startDate: string;
};
type ProfileDraft = {
    cid: string;
    dob: string;
    optionFacilityId: string;
    program: string;
    provider: string;
};
type PersonDraft = {
    confirmEmail: string;
    email: string;
    firstname: string;
    lastname: string;
    title: string;
};
type StatusDraft = {
    activationBackDate: string;
    otherReason: string;
    reason: string;
};
type MedicationDraft = {
    administrationNotes: string;
    customDays: string[];
    dateDue: string;
    dateGiven: string;
    dateInjectionArrived: string;
    frequency: string;
    importanceLevel: string;
    medicationExternalId: string;
    medicineDetail: string;
    medicineName: string;
    onSite: string;
    prn: boolean;
    psychotropicMeds: boolean;
    refillDate: string;
    refillStatus: boolean;
    route: string;
    startDate: string;
    stopDate: string;
    stopDateEnabled: boolean;
    strength: string;
    timesPerDay: string;
};
const normalizeValue = (value: unknown) => `${value || ''}`.trim();
const normalizeEmail = (value: unknown) => normalizeValue(value).toLowerCase();
const padDatePart = (value: number) => `${value}`.padStart(2, '0');
const parseDisplayDate = (value: string) => {
    const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/);
    if (isoMatch) {
        return {
            day: isoMatch[3],
            hour: isoMatch[4] ? Number(isoMatch[4]) : null,
            minute: isoMatch[5] || null,
            month: isoMatch[2],
            year: isoMatch[1],
        };
    }
    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) {
        return null;
    }
    return {
        day: padDatePart(parsedDate.getUTCDate()),
        hour: parsedDate.getUTCHours(),
        minute: padDatePart(parsedDate.getUTCMinutes()),
        month: padDatePart(parsedDate.getUTCMonth() + 1),
        year: `${parsedDate.getUTCFullYear()}`,
    };
};
const formatDate = (value: string | null | undefined) => {
    const normalizedValue = normalizeValue(value);
    const displayDate = parseDisplayDate(normalizedValue);
    if (!displayDate) {
        return normalizedValue || '—';
    }
    return `${displayDate.month}/${displayDate.day}/${displayDate.year}`;
};
const formatDateTime = (value: string | null | undefined) => {
    const normalizedValue = normalizeValue(value);
    const displayDate = parseDisplayDate(normalizedValue);
    if (!displayDate) {
        return normalizedValue || '—';
    }
    const dateLabel = `${displayDate.month}/${displayDate.day}/${displayDate.year}`;
    if (displayDate.hour === null || !displayDate.minute) {
        return dateLabel;
    }
    const hourValue = displayDate.hour;
    const minuteValue = displayDate.minute;
    const suffix = hourValue >= 12 ? 'PM' : 'AM';
    const normalizedHour = hourValue % 12 || 12;
    return `${dateLabel} ${normalizedHour}:${minuteValue} ${suffix}`;
};
const formatPersonLabel = (person: MedcallPerson | null | undefined) => {
    if (!person) {
        return '—';
    }
    const pieces = [normalizeValue(person.firstname), normalizeValue(person.lastname)].filter(Boolean);
    const title = normalizeValue(person.title);
    if (title) {
        pieces.push(title);
    }
    if (pieces.length < 1) {
        return '—';
    }
    return pieces.join(', ');
};
const formatLegacyPersonLabel = (person: MedcallPerson | null | undefined) => {
    if (!person) {
        return '—';
    }
    const name = [normalizeValue(person.firstname), normalizeValue(person.lastname)]
        .filter(Boolean)
        .join(' ');
    const title = normalizeValue(person.title);
    return [name, title].filter(Boolean).join(', ') || '—';
};
const formatAliases = (aliases: MedcallAlias[] | null | undefined) => {
    if (!Array.isArray(aliases) || aliases.length < 1) {
        return '—';
    }
    const aliasLabels = aliases
        .map((alias) => [normalizeValue(alias.firstname), normalizeValue(alias.lastname)].filter(Boolean).join(' '))
        .filter(Boolean);
    if (aliasLabels.length < 1) {
        return '—';
    }
    return aliasLabels.join(', ');
};
const formatMedicationFrequency = (medication: MedcallParticipantMedication) => {
    const frequency = normalizeValue(medication.frequency).toLowerCase();
    if (!frequency) {
        return '—';
    }
    if (frequency === 'custom_days') {
        const days = Array.isArray(medication.days_of_week) ? medication.days_of_week.filter(Boolean).join(', ') : '';
        const timesPerDay = normalizeValue(medication.times_per_day);
        if (days && timesPerDay) {
            return `${days} • ${timesPerDay}`;
        }
        if (days) {
            return days;
        }
    }
    if (frequency === 'everyday') {
        return normalizeValue(medication.times_per_day) || 'Every day';
    }
    if (frequency === 'everyotherday') {
        return 'Every other day';
    }
    return frequency.replace(/_/g, ' ');
};
const legacyMedicationSignature = (medication: MedcallParticipantMedication) => [
    medication.medicine_name,
    medication.strength,
    medication.route,
    medication.frequency,
    medication.times_per_day,
    medication.days_of_week,
    medication.prn,
]
    .map((value) => normalizeValue(Array.isArray(value) || (value && typeof value === 'object')
    ? JSON.stringify(value)
    : value).replace(/\s+/g, ' ').toLowerCase())
    .join('|');
const legacyMedicationHasStopDate = (medication: MedcallParticipantMedication) => (Boolean(Number(medication.stop_date_status)) || Boolean(normalizeValue(medication.stop_date)));
const getLegacyMedicationDisplayName = (medication: MedcallParticipantMedication) => {
    const medicineName = normalizeValue(medication.medicine_name);
    const detailName = normalizeValue(Array.isArray(medication.medicine_detail)
        ? medication.medicine_detail[0]?.name
        : '');
    const doseOnlyPattern = /^\s*\d+(?:\.\d+)?\s*(?:mg|mcg|g|kg|ml|l|unit|units)?(?:\s*\/\s*(?:\d+(?:\.\d+)?\s*)?(?:mg|mcg|g|kg|ml|l|unit|units))?\s*$/i;
    return doseOnlyPattern.test(medicineName) && detailName
        ? detailName
        : medicineName || detailName || '—';
};
const getLegacyMedicationDose = (medication: MedcallParticipantMedication) => {
    const medicineName = normalizeValue(medication.medicine_name);
    const strength = normalizeValue(medication.strength);
    if (/^\d+(?:\.\d+)?$/.test(strength)) {
        const doseAtEnd = medicineName.match(/(\d+(?:\.\d+)?\s*(?:mg|mcg|g|kg|ml|mL|l|L|unit|units)(?:\s*\/\s*(?:\d+(?:\.\d+)?\s*)?(?:mg|mcg|g|kg|ml|mL|l|L|unit|units))?)\s*$/i);
        const doseNumber = doseAtEnd?.[1]?.match(/^\d+(?:\.\d+)?/);
        if (doseAtEnd?.[1] && doseNumber && Number(doseNumber[0]) === Number(strength)) {
            return doseAtEnd[1].replace(/\s+/g, ' ');
        }
    }
    return strength || medicineName || '—';
};
const getLegacyMedicationFrequency = (medication: MedcallParticipantMedication) => {
    const frequency = normalizeValue(medication.frequency).toLowerCase();
    if (frequency === 'everyotherday') {
        return 'QOD';
    }
    if (frequency === 'custom_days') {
        const days = Array.isArray(medication.days_of_week)
            ? medication.days_of_week.filter(Boolean).join(', ')
            : '';
        return [days, normalizeValue(medication.times_per_day)].filter(Boolean).join(' - ').toUpperCase() || '—';
    }
    if (frequency === 'everyday') {
        return normalizeValue(medication.times_per_day).toUpperCase() || '—';
    }
    return frequency.toUpperCase() || '—';
};
const MedicationColumn = ({ children }: { children: React.ReactNode }) => <>{children}</>;
const formatDateForInput = (value: string | null | undefined) => {
    const normalizedValue = normalizeValue(value);
    const match = normalizedValue.match(/^(\d{4}-\d{2}-\d{2})/);
    if (!match) {
        return '';
    }
    return match[1];
};
const formatDateTimeForInput = (value: string | null | undefined) => {
    const normalizedValue = normalizeValue(value);
    const match = normalizedValue.match(/^(\d{4}-\d{2}-\d{2})(?:[ T](\d{2}):(\d{2}))?/);
    if (!match) {
        return '';
    }
    return `${match[1]}T${match[2] || '00'}:${match[3] || '00'}`;
};
const formatImportanceLabel = (value: number | null | undefined) => {
    if (value === 2) {
        return 'High';
    }
    if (value === 1) {
        return 'Medium';
    }
    if (value === 0) {
        return 'Low';
    }
    return '—';
};
const getActiveBadgeStyle = (isActive: boolean): React.CSSProperties => {
    if (isActive) {
        return {
            borderColor: 'var(--semantic-success)',
            backgroundColor: 'var(--surface-panel-solid)',
            color: 'var(--semantic-success)',
        };
    }
    return {
        borderColor: 'var(--neutral-gray-40)',
        backgroundColor: 'var(--surface-muted)',
        color: 'var(--neutral-gray-100)',
    };
};
const warningBadgeStyle: React.CSSProperties = {
    backgroundColor: 'var(--semantic-warning-soft)',
    borderColor: 'var(--semantic-warning-border-soft)',
    color: 'var(--semantic-warning)',
};
const errorPanelStyle: React.CSSProperties = {
    backgroundColor: 'var(--semantic-error-soft)',
    borderColor: 'var(--semantic-error-border-soft)',
    color: 'var(--semantic-error)',
};
const getLosAngelesDateOnly = (value: Date = new Date()) => {
    const parts = new Intl.DateTimeFormat('en-US', {
        day: '2-digit',
        month: '2-digit',
        timeZone: 'America/Los_Angeles',
        year: 'numeric',
    }).formatToParts(value);
    const getPart = (type: string) => parts.find((part) => part.type === type)?.value || '';
    return `${getPart('year')}-${getPart('month')}-${getPart('day')}`;
};
const getLosAngelesDateOnlyWithOffset = (dayOffset: number) => {
    const nextDate = new Date();
    nextDate.setUTCDate(nextDate.getUTCDate() + dayOffset);
    return getLosAngelesDateOnly(nextDate);
};
const isBlockedEmailDomain = (email: string, filterDomains: string[]) => {
    const normalizedEmail = normalizeEmail(email);
    if (!normalizedEmail.includes('@')) {
        return false;
    }
    const domain = normalizedEmail.split('@')[1] || '';
    return filterDomains.includes(domain);
};
const buildProfileDraft = (participant: MedcallParticipantDetail): ProfileDraft => ({
    cid: normalizeValue(participant.cid),
    dob: normalizeValue(participant.dob),
    optionFacilityId: participant.option_facility_id ? `${participant.option_facility_id}` : '',
    program: normalizeValue(participant.program),
    provider: normalizeValue(participant.provider),
});
const buildPersonDraft = (person: MedcallPerson | null | undefined): PersonDraft => ({
    confirmEmail: normalizeValue(person?.email),
    email: normalizeValue(person?.email),
    firstname: normalizeValue(person?.firstname),
    lastname: normalizeValue(person?.lastname),
    title: normalizeValue(person?.title),
});
const buildStatusDraft = (): StatusDraft => ({
    activationBackDate: getLosAngelesDateOnly(),
    otherReason: '',
    reason: DEACTIVATION_OPTIONS[0],
});
const MEDICATION_EMPTY_DRAFT: MedicationDraft = {
    administrationNotes: '',
    customDays: [],
    dateDue: '',
    dateGiven: '',
    dateInjectionArrived: '',
    frequency: '',
    importanceLevel: '2',
    medicationExternalId: '',
    medicineDetail: '',
    medicineName: '',
    onSite: '0',
    prn: false,
    psychotropicMeds: false,
    refillDate: '',
    refillStatus: true,
    route: '',
    startDate: '',
    stopDate: '',
    stopDateEnabled: false,
    strength: '',
    timesPerDay: '',
};
const buildMedicationDraft = (medication?: MedcallParticipantMedication | null): MedicationDraft => {
    if (!medication) {
        return { ...MEDICATION_EMPTY_DRAFT };
    }
    const injectionInfo = Array.isArray(medication.injection_info) ? medication.injection_info[0] : null;
    const refillEntry = Array.isArray(medication.refills) ? medication.refills[0] : null;
    const route = normalizeValue(medication.route);
    const frequency = normalizeValue(medication.frequency).toLowerCase();
    const defaultTimesPerDay = route === 'IM-LAI' ? 'qday' : '';
    return {
        administrationNotes: normalizeValue(injectionInfo?.administration_notes),
        customDays: Array.isArray(medication.days_of_week) ? medication.days_of_week.filter(Boolean) : [],
        dateDue: formatDateForInput(injectionInfo?.date_due),
        dateGiven: formatDateForInput(injectionInfo?.date_given),
        dateInjectionArrived: formatDateForInput(injectionInfo?.date_injection_arrived),
        frequency,
        importanceLevel: Number.isInteger(Number(medication.importance_level))
            ? `${Number(medication.importance_level)}`
            : '2',
        medicationExternalId: resolveStoredMedicationExternalId({
            medicineDetail: medication.medicine_detail,
            uuid: medication.uuid,
        }),
        medicineDetail: JSON.stringify(Array.isArray(medication.medicine_detail) ? medication.medicine_detail : []),
        medicineName: normalizeValue(medication.medicine_name),
        onSite: Number.isInteger(Number(injectionInfo?.on_site)) ? `${Number(injectionInfo?.on_site)}` : '0',
        prn: Boolean(medication.prn),
        psychotropicMeds: Boolean(medication.psychotropic_meds),
        refillDate: formatDateForInput(refillEntry?.refill_date),
        refillStatus: Boolean(medication.refill_status),
        route,
        startDate: formatDateTimeForInput(medication.start_date),
        stopDate: formatDateTimeForInput(medication.stop_date),
        stopDateEnabled: normalizeValue(medication.stop_date_status) === '1',
        strength: normalizeValue(medication.strength),
        timesPerDay: normalizeValue(medication.times_per_day).toLowerCase() || defaultTimesPerDay,
    };
};
const participantProgramUsesFacility = (program: string) => !NURSELESS_PROGRAMS.includes(program);
const DetailField = ({ compact = false, label, value }: {
    compact?: boolean;
    label: string;
    value: React.ReactNode;
}) => {
    return (<div className={compact ? 'min-w-0 space-y-0.5' : 'space-y-1'}>
      <div className={`${compact ? 'text-xs' : 'text-sm tracking-wide'} truncate font-medium uppercase text-[var(--neutral-gray-90)]`}>{label}</div>
      <div className={`${compact ? 'truncate whitespace-nowrap' : ''} text-sm text-[var(--neutral-gray-120)]`} title={typeof value === 'string' ? value : undefined}>
        {value}
      </div>
    </div>);
};
const DialogField = ({ children, label, required = false, }: {
    children: React.ReactNode;
    label: string;
    required?: boolean;
}) => {
    return (<label className="space-y-2">
      <div className="text-sm font-medium text-[var(--neutral-gray-120)]">
        {label}
        {required ? <span className="ml-1 text-[var(--semantic-error)]">*</span> : null}
      </div>
      {children}
    </label>);
};
const getMedicationFrequencyOptions = (route: string) => {
    if (route !== 'IM-LAI') {
        return MEDICATION_FREQUENCY_OPTIONS;
    }
    return MEDICATION_FREQUENCY_OPTIONS.filter((option) => !IM_LAI_FILTERED_FREQUENCIES.has(option.value));
};
const getMedicationScheduleOptions = (frequency: string) => {
    const matchedOption = MEDICATION_FREQUENCY_OPTIONS.find((option) => option.value === frequency) || null;
    if (!matchedOption) {
        return [];
    }
    return matchedOption.scheduleOptions;
};
const formatMedicationScheduleLabel = (value: string) => MEDICATION_TIME_OPTION_LABELS[value] || value.toUpperCase();
const isMedicationScatteredErcProgram = (program: string) => NURSELESS_PROGRAMS.includes(program);
const isProjectSitesProgram = (program: string) => program === 'Project Sites';
const formatTimelineStatusLabel = (value: string | null | undefined) => {
    const normalizedValue = normalizeValue(value);
    if (!normalizedValue) {
        return 'Unset';
    }
    if (normalizedValue === 'notTaken') {
        return 'Not Taken';
    }
    if (normalizedValue === 'refilled') {
        return 'Refilled';
    }
    return normalizedValue.charAt(0).toUpperCase() + normalizedValue.slice(1);
};
const formatInjectionOnSiteLabel = (value: number | null | undefined, preserveLegacySemantics = false) => {
    if (preserveLegacySemantics) {
        return ['Not Yet On-site', 'N.A.', 'On-Site'][Number(value)] || '';
    }
    if (value === 2) {
        return 'Given';
    }
    if (value === 1) {
        return 'On Site';
    }
    if (value === 0) {
        return 'Pending';
    }
    return '—';
};
const formatTimeLabel = (value: string | null | undefined) => {
    const normalizedValue = normalizeValue(value);
    const match = normalizedValue.match(/^(\d{2}):(\d{2})/);
    if (!match) {
        return normalizedValue || '—';
    }
    const hourValue = Number(match[1]);
    const minuteValue = match[2];
    const suffix = hourValue >= 12 ? 'PM' : 'AM';
    const normalizedHour = hourValue % 12 || 12;
    return `${normalizedHour}:${minuteValue} ${suffix}`;
};
const formatMedicationTimelineDoseLabel = (medication: MedcallParticipantMedication, timeline: MedcallMedicationTimeline) => {
    const pieces = [
        normalizeValue(timeline.dose) ? `${normalizeValue(timeline.dose)} dose` : '',
        normalizeValue(medication.route) || normalizeValue(timeline.route),
        normalizeValue(medication.strength) || normalizeValue(timeline.strength),
    ].filter(Boolean);
    if (pieces.length < 1) {
        return formatMedicationFrequency(medication);
    }
    return pieces.join(' • ');
};
const parseUtcDateOnly = (value: string) => {
    const match = normalizeValue(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) {
        return null;
    }
    return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
};
const formatUtcDateOnly = (value: Date) => {
    const year = value.getUTCFullYear();
    const month = `${value.getUTCMonth() + 1}`.padStart(2, '0');
    const day = `${value.getUTCDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
};
const addUtcDays = (value: Date, dayOffset: number) => (new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate() + dayOffset)));
const waitForLegacyTimelinePatch = () => new Promise<void>((resolve) => {
    window.setTimeout(resolve, 500);
});
const getUtcMonthStart = (value: Date) => (new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), 1)));
const getUtcMonthEnd = (value: Date) => (new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth() + 1, 0)));
const getUtcWeekStart = (value: Date, weekStart: number) => {
    const normalizedWeekStart = ((weekStart % 7) + 7) % 7;
    const currentDay = value.getUTCDay();
    const dayOffset = (currentDay - normalizedWeekStart + 7) % 7;
    return addUtcDays(value, -dayOffset);
};
const getUtcWeekEnd = (value: Date, weekStart: number) => addUtcDays(getUtcWeekStart(value, weekStart), 6);
const getProjectSitesMonthGridDates = (monthValue: string, weekStart: number) => {
    const selectedMonth = parseUtcDateOnly(`${monthValue}-01`);
    if (!selectedMonth) {
        return [];
    }
    const monthStart = getUtcMonthStart(selectedMonth);
    const monthEnd = getUtcMonthEnd(selectedMonth);
    const rangeStart = getUtcWeekStart(monthStart, weekStart);
    const rangeEnd = getUtcWeekEnd(monthEnd, weekStart);
    const dates: Date[] = [];
    let currentDate = rangeStart;
    while (currentDate.getTime() <= rangeEnd.getTime()) {
        dates.push(currentDate);
        currentDate = addUtcDays(currentDate, 1);
    }
    return dates;
};
const getProjectSitesMonthWeekRanges = (monthValue: string, weekStart: number) => {
    const selectedMonth = parseUtcDateOnly(`${monthValue}-01`);
    if (!selectedMonth) {
        return [];
    }
    const monthStart = getUtcMonthStart(selectedMonth);
    const monthEnd = getUtcMonthEnd(selectedMonth);
    const rangeStart = getUtcWeekStart(monthStart, weekStart);
    const weekRanges: Array<{
        endDate: string;
        label: string;
        startDate: string;
    }> = [];
    let currentDate = rangeStart;
    while (currentDate.getTime() <= monthEnd.getTime()) {
        const weekStartDate = currentDate;
        const weekEndDate = getUtcWeekEnd(currentDate, weekStart);
        weekRanges.push({
            endDate: formatUtcDateOnly(weekEndDate),
            label: `${formatDate(formatUtcDateOnly(weekStartDate))} - ${formatDate(formatUtcDateOnly(weekEndDate))}`,
            startDate: formatUtcDateOnly(weekStartDate),
        });
        currentDate = addUtcDays(weekEndDate, 1);
    }
    return weekRanges;
};
const getProjectSitesYearMonths = (monthValue: string) => {
    const selectedMonth = parseUtcDateOnly(`${monthValue}-01`);
    if (!selectedMonth) {
        return [];
    }
    return Array.from({ length: 12 }, (_, index) => {
        const monthDate = new Date(Date.UTC(selectedMonth.getUTCFullYear(), index, 1));
        const monthLabel = new Intl.DateTimeFormat('en-US', {
            month: 'long',
            timeZone: 'UTC',
        }).format(monthDate);
        return {
            endDate: formatUtcDateOnly(getUtcMonthEnd(monthDate)),
            label: monthLabel,
            startDate: formatUtcDateOnly(monthDate),
        };
    });
};
const getProjectSitesStatusLabel = (statusLookup: Map<string, MedcallProjectSitesMedicationStatus>, startDate: string, endDate: string) => {
    const start = parseUtcDateOnly(startDate);
    const end = parseUtcDateOnly(endDate);
    if (!start || !end) {
        return 'Not Taken';
    }
    let currentDate = start;
    while (currentDate.getTime() <= end.getTime()) {
        const currentStatus = statusLookup.get(formatUtcDateOnly(currentDate));
        if (currentStatus?.status !== 'taken') {
            return 'Not Taken';
        }
        currentDate = addUtcDays(currentDate, 1);
    }
    return 'Taken';
};
export const MedcallWorkspaceParticipant = ({ bootstrap, embedded = false, odrparticipant, routeBase = '/tools/medcall-workspace', }: {
    bootstrap: MedcallBootstrapContext;
    embedded?: boolean;
    odrparticipant?: number;
    routeBase?: string;
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { participantId } = useParams<{
        participantId: string;
    }>();
    const locationState = (location.state || {}) as RouteLocationState;
    const requestedRouteDialog = locationState.openDialog === 'medication' || locationState.openDialog === 'message'
        ? locationState.openDialog
        : null;
    const normalizedParticipantId = Number(participantId || 0);
    const normalizedOdrparticipant = Number(odrparticipant || 0);
    const isValidParticipantId = Number.isInteger(normalizedParticipantId) && normalizedParticipantId > 0;
    const isValidOdrparticipant = Number.isInteger(normalizedOdrparticipant) && normalizedOdrparticipant > 0;
    const hasValidParticipantLookup = isValidParticipantId || isValidOdrparticipant;
    const bootstrapBackPath = `${bootstrap.defaultLandingPath || ''}`.startsWith('/tools/medcall')
        ? `${routeBase}${`${bootstrap.defaultLandingPath || ''}`.slice('/tools/medcall'.length)}`
        : bootstrap.defaultLandingPath;
    const backPath = locationState.from || bootstrapBackPath || `${routeBase}/all-clients`;
    const isMedcall2Page = false;
    const { data: legacyOptionData } = useQuery<{
        medcallOptions: Medcall2OptionRow[];
    }>(MEDCALL2_PROFILE_OPTIONS, {
        fetchPolicy: 'network-only',
        skip: !false || !hasValidParticipantLookup,
    });
    const { data: countyHolidayData } = useQuery<MedcallCountyHolidaysData>(MEDCALL_COUNTY_HOLIDAYS, {
        fetchPolicy: 'cache-first',
        skip: !false,
    });
    const { data, error, loading, refetch } = useQuery<MedcallParticipantDetailData>(MEDCALL_PARTICIPANT_DETAIL_PAGE, {
        fetchPolicy: 'cache-and-network',
        skip: !hasValidParticipantLookup,
        variables: {
            includeTimelines: !embedded,
            odrparticipant: isValidOdrparticipant ? normalizedOdrparticipant : null,
            participantId: isValidParticipantId ? normalizedParticipantId : null,
            preserveLegacyMedicationOrder: false,
        },
    });
    const [addToMyClients] = useMutation(MEDCALL_ADD_TO_MY_CLIENTS);
    const [removeFromMyClients] = useMutation(MEDCALL_REMOVE_FROM_MY_CLIENTS);
    const [updateParticipantProfile] = useMutation(MEDCALL_UPDATE_PARTICIPANT_PROFILE);
    const [updateParticipantStaff] = useMutation(MEDCALL_UPDATE_PARTICIPANT_STAFF);
    const [updateParticipantNurse] = useMutation(MEDCALL_UPDATE_PARTICIPANT_NURSE);
    const [updateParticipantPsychiatrist] = useMutation(MEDCALL_UPDATE_PARTICIPANT_PSYCHIATRIST);
    const [addParticipantNote] = useMutation(MEDCALL_ADD_PARTICIPANT_NOTE);
    const [addParticipantAlias] = useMutation(MEDCALL_ADD_PARTICIPANT_ALIAS);
    const [deleteParticipantNote] = useMutation(MEDCALL_DELETE_PARTICIPANT_NOTE);
    const [setParticipantActive] = useMutation(MEDCALL_SET_PARTICIPANT_ACTIVE);
    const [createMedication] = useMutation(MEDCALL_CREATE_MEDICATION);
    const [updateMedication] = useMutation(MEDCALL_UPDATE_MEDICATION);
    const [updateImlaiDueDate] = useMutation(MEDCALL_UPDATE_IMLAI_DUE_DATE);
    const [setMedicationActive] = useMutation(MEDCALL_SET_MEDICATION_ACTIVE);
    const [deleteMedication] = useMutation(MEDCALL_DELETE_MEDICATION);
    const [updateMedicationTimeline] = useMutation(MEDCALL_UPDATE_MEDICATION_TIMELINE, {
        fetchPolicy: undefined,
    });
    const [updateMedicationTimelineSite] = useMutation(MEDCALL_UPDATE_MEDICATION_TIMELINE_SITE, {
        fetchPolicy: undefined,
    });
    const [updateMedicationAdministrationNotes] = useMutation(MEDCALL_UPDATE_MEDICATION_ADMINISTRATION_NOTES, {
        fetchPolicy: undefined,
    });
    const [updateProjectSitesMedicationStatuses] = useMutation(MEDCALL_UPDATE_PROJECT_SITES_MEDICATION_STATUSES);
    const [updateProjectSitesClientOptions] = useMutation(MEDCALL_UPDATE_PROJECT_SITES_CLIENT_OPTIONS);
    const [loadMedicationTimelines] = useLazyQuery(MEDCALL_MEDICATION_TIMELINES, {
        fetchPolicy: 'network-only',
    });
    const [searchMedicationCatalog] = useLazyQuery<MedcallMedicationSearchData>(MEDCALL_SEARCH_MEDICATIONS, {
        fetchPolicy: 'network-only',
    });
    const [loadMedicationStrengths] = useLazyQuery<MedcallMedicationStrengthsData>(MEDCALL_MEDICATION_STRENGTHS, {
        fetchPolicy: 'network-only',
    });
    const [pendingAction, setPendingAction] = React.useState<string | null>(null);
    const [activeDialog, setActiveDialog] = React.useState<'aliases' | 'edit' | 'med-list' | 'medication' | 'message' | 'nurse' | 'notes' | 'psychiatrist' | 'report' | 'staff' | 'status' | null>(requestedRouteDialog);
    const [medicationView, setMedicationView] = React.useState<MedcallMedicationView>('active');
    const [openMedicationMenuId, setOpenMedicationMenuId] = React.useState<number | null>(null);
    const [expandedMedicationIds, setExpandedMedicationIds] = React.useState<Set<number>>(() => new Set());
    const [medicationTimelineHistory, setMedicationTimelineHistory] = React.useState<Map<number, MedcallMedicationTimeline[]>>(() => new Map());
    const [medicationTimelinePages, setMedicationTimelinePages] = React.useState<Map<number, number>>(() => new Map());
    const [medicationTimelineOverrides, setMedicationTimelineOverrides] = React.useState<Map<number, Partial<MedcallMedicationTimeline>>>(() => new Map());
    const [projectSitesTrackerView, setProjectSitesTrackerView] = React.useState<ProjectSitesTrackerView>('day');
    const [projectSitesTrackerMonth, setProjectSitesTrackerMonth] = React.useState(() => getLosAngelesDateOnly().slice(0, 7));
    const [projectSitesTrackerSelection, setProjectSitesTrackerSelection] = React.useState<ProjectSitesTrackerSelection | null>(null);
    const [projectSitesTrackerStatus, setProjectSitesTrackerStatus] = React.useState<'notTaken' | 'taken'>('taken');
    const [projectSitesTrackerNotes, setProjectSitesTrackerNotes] = React.useState('');
    const [projectSitesWeekStartDraft, setProjectSitesWeekStartDraft] = React.useState('1');
    const [staffDialogTab, setStaffDialogTab] = React.useState('form');
    const [profileDraft, setProfileDraft] = React.useState<ProfileDraft>({
        cid: '',
        dob: '',
        optionFacilityId: '',
        program: '',
        provider: '',
    });
    const [staffDraft, setStaffDraft] = React.useState('');
    const [nurseDraft, setNurseDraft] = React.useState<PersonDraft>({
        confirmEmail: '',
        email: '',
        firstname: '',
        lastname: '',
        title: '',
    });
    const [psychiatristDraft, setPsychiatristDraft] = React.useState<PersonDraft>({
        confirmEmail: '',
        email: '',
        firstname: '',
        lastname: '',
        title: '',
    });
    const [noteDraft, setNoteDraft] = React.useState('');
    const [aliasDraft, setAliasDraft] = React.useState({ firstname: '', lastname: '' });
    const [deleteNoteTarget, setDeleteNoteTarget] = React.useState<MedcallParticipantNote | null>(null);
    const [statusDraft, setStatusDraft] = React.useState<StatusDraft>(buildStatusDraft());
    const [editingMedicationId, setEditingMedicationId] = React.useState<number | null>(null);
    const [medicationDraft, setMedicationDraft] = React.useState<MedicationDraft>({ ...MEDICATION_EMPTY_DRAFT });
    const [medicationPreviewVariables, setMedicationPreviewVariables] = React.useState<Record<string, any> | null>(null);
    const [medicationSearchResults, setMedicationSearchResults] = React.useState<MedcallMedicationSearchItem[]>([]);
    const [medicationStrengthOptions, setMedicationStrengthOptions] = React.useState<MedcallMedicationStrengthOption[]>([]);
    const [medicationSearchLoading, setMedicationSearchLoading] = React.useState(false);
    const [usesCustomMedicationStrength, setUsesCustomMedicationStrength] = React.useState(false);
    const [legacyMedicationSelected, setLegacyMedicationSelected] = React.useState(false);
    const [dismissedImlaiWarningParticipantId, setDismissedImlaiWarningParticipantId] = React.useState(0);
    const routeDialogHandledRef = React.useRef('');
    const medicationPreviewSubmittingRef = React.useRef(false);
    const activeParticipantId = Number(data?.medcallParticipantDetail?.id || 0);
    const activeParticipantWeekStart = Number.isInteger(Number(data?.medcallParticipantDetail?.project_sites_client_options?.week_start))
        ? Number(data?.medcallParticipantDetail?.project_sites_client_options?.week_start)
        : 1;
    React.useEffect(() => {
        setProjectSitesWeekStartDraft(`${activeParticipantWeekStart}`);
    }, [activeParticipantId, activeParticipantWeekStart]);
    React.useEffect(() => {
        setDismissedImlaiWarningParticipantId(0);
    }, [activeParticipantId]);
    React.useEffect(() => {
        setMedicationView('active');
        setOpenMedicationMenuId(null);
        setExpandedMedicationIds(new Set());
        setMedicationTimelineHistory(new Map());
        setMedicationTimelinePages(new Map());
        setMedicationTimelineOverrides(new Map());
        setProjectSitesTrackerSelection(null);
        setProjectSitesTrackerStatus('taken');
        setProjectSitesTrackerNotes('');
    }, [activeParticipantId, false]);
    React.useEffect(() => {
        if (!requestedRouteDialog || activeParticipantId < 1) {
            return;
        }
        const requestKey = `${activeParticipantId}:${requestedRouteDialog}`;
        if (routeDialogHandledRef.current === requestKey) {
            return;
        }
        routeDialogHandledRef.current = requestKey;
        if (requestedRouteDialog === 'medication') {
            const participantProgram = normalizeValue(data?.medcallParticipantDetail?.program);
            setEditingMedicationId(null);
            setMedicationSearchResults([]);
            setMedicationStrengthOptions([]);
            setUsesCustomMedicationStrength(false);
            setLegacyMedicationSelected(false);
            setMedicationDraft({
                ...MEDICATION_EMPTY_DRAFT,
                refillStatus: !isMedicationScatteredErcProgram(participantProgram),
            });
        }
        setActiveDialog(requestedRouteDialog);
        navigate(location.pathname, {
            replace: true,
            state: {
                from: locationState.from,
            },
        });
    }, [
        activeParticipantId,
        data?.medcallParticipantDetail?.program,
        location.pathname,
        locationState.from,
        navigate,
        requestedRouteDialog,
    ]);
    React.useEffect(() => {
        if (activeDialog !== 'medication') {
            return;
        }
        const searchText = normalizeValue(medicationDraft.medicineName);
        if (searchText.length < 4 || medicationDraft.medicationExternalId) {
            setMedicationSearchResults([]);
            setMedicationSearchLoading(false);
            return;
        }
        let isCurrentRequest = true;
        const timeoutId = window.setTimeout(async () => {
            setMedicationSearchLoading(true);
            try {
                const result = await searchMedicationCatalog({ variables: { searchText } });
                if (!isCurrentRequest) {
                    return;
                }
                const searchItems = result.data?.medcallSearchMedications;
                setMedicationSearchResults(Array.isArray(searchItems)
                    ? searchItems.filter(Boolean)
                    : []);
            }
            catch (searchError: any) {
                if (!isCurrentRequest) {
                    return;
                }
                setMedicationSearchResults([]);
                toast.error(searchError?.message || 'Unable to search medications.');
            }
            if (isCurrentRequest) {
                setMedicationSearchLoading(false);
            }
        }, 300);
        return () => {
            isCurrentRequest = false;
            window.clearTimeout(timeoutId);
        };
    }, [activeDialog, medicationDraft.medicationExternalId, medicationDraft.medicineName, searchMedicationCatalog]);
    const toggleMedicationHistory = React.useCallback(async (medicationId: number) => {
        if (embedded && !medicationTimelineHistory.has(medicationId)) {
            try {
                const result = await loadMedicationTimelines({ variables: { medicationId } });
                const timelines = Array.isArray(result.data?.medcallMedicationTimelines)
                    ? result.data.medcallMedicationTimelines
                    : [];
                setMedicationTimelineHistory((current) => new Map(current).set(medicationId, timelines));
            }
            catch (timelineError: any) {
                toast.error(timelineError?.message || 'Unable to load medication history.');
                return;
            }
        }
        setExpandedMedicationIds((previousIds) => {
            const nextIds = new Set(previousIds);
            if (nextIds.has(medicationId)) {
                nextIds.delete(medicationId);
                return nextIds;
            }
            nextIds.add(medicationId);
            return nextIds;
        });
    }, [embedded, loadMedicationTimelines, medicationTimelineHistory]);
    if (!hasValidParticipantLookup) {
        return (<Card>
        <CardContent className="p-[var(--panel-padding)]">
          <div className="text-sm text-[var(--neutral-gray-100)]">Client not found.</div>
        </CardContent>
      </Card>);
    }
    if (loading) {
        return <OdrLoadingSpinner />;
    }
    if (error) {
        return (<Card style={errorPanelStyle}>
        <CardContent className="p-[var(--panel-padding)]">
          <div className="text-sm">{error.message}</div>
        </CardContent>
      </Card>);
    }
    const participant = data?.medcallParticipantDetail;
    if (!participant) {
        return (<Card>
        <CardContent className="p-[var(--panel-padding)]">
          <div className="text-sm text-[var(--neutral-gray-100)]">Client not found.</div>
        </CardContent>
      </Card>);
    }
    const actionMeta = data?.medcallParticipantActionMeta;
    const legacyOptions = readMedcall2IntakeOptions(legacyOptionData?.medcallOptions);
    const facilityOptions: MedcallListOption[] = Array.isArray(actionMeta?.facilityOptions) ? actionMeta?.facilityOptions || [] : [];
    const filterDomains: string[] = Array.isArray(actionMeta?.filterDomains)
        ? (actionMeta?.filterDomains || []).map((value) => normalizeValue(value).toLowerCase()).filter(Boolean)
        : [];
    const programOptions: MedcallListOption[] = Array.isArray(actionMeta?.programOptions) ? actionMeta?.programOptions || [] : [];
    const providerOptions = (() => {
        ;
        const seen = new Set<string>();
        const options: Array<{
            label: string;
            value: string;
        }> = [];
        const pushOption = (labelValue: string) => {
            const normalized = normalizeValue(labelValue);
            if (!normalized || seen.has(normalized)) {
                return;
            }
            seen.add(normalized);
            options.push({
                label: normalized,
                value: normalized,
            });
        };
        const metaOptions: MedcallListOption[] = Array.isArray(actionMeta?.providerOptions)
            ? actionMeta?.providerOptions || []
            : [];
        metaOptions.forEach((option) => {
            pushOption(normalizeValue(option.value) || normalizeValue(option.label));
        });
        pushOption(participant.provider || '');
        return options;
    })();
    const staffOptions = (() => {
        const seen = new Set<number>();
        const options: MedcallStaffOption[] = [];
        const pushOption = (option: MedcallStaffOption | null) => {
            if (!option) {
                return;
            }
            const optionId = Number(option.id || 0);
            if (!Number.isInteger(optionId) || optionId < 1 || seen.has(optionId)) {
                return;
            }
            seen.add(optionId);
            options.push({
                email: normalizeValue(option.email) || null,
                fullname: normalizeValue(option.fullname) || null,
                id: optionId,
                username: normalizeValue(option.username) || null,
            });
        };
        const metaOptions: MedcallStaffOption[] = Array.isArray(actionMeta?.staffOptions)
            ? actionMeta?.staffOptions || []
            : [];
        metaOptions.forEach((option) => pushOption(option));
        if (!false && participant.user_id && participant.user_id > 0) {
            pushOption({
                email: null,
                fullname: normalizeValue(participant.staff_assigned) || `Staff ${participant.user_id}`,
                id: participant.user_id,
                username: null,
            });
        }
        return orderMedcallStaffOptions(options, routeBase);
    })();
    const isOnMyClients = Array.isArray(participant.view_user_ids)
        ? participant.view_user_ids.includes(`${bootstrap.medcallUserId}`)
        : false;
    const medications = Array.isArray(participant.medications) ? participant.medications : [];
    const configuredCountyHolidays = countyHolidayData?.medcallCountyHolidays;
    const countyHolidays = Array.isArray(configuredCountyHolidays)
        ? configuredCountyHolidays.filter(Boolean) as Array<{
            date?: string | null;
            holiday?: string | null;
        }>
        : [];
    const projectSitesMedicationStatuses = Array.isArray(participant.project_sites_medication_statuses)
        ? participant.project_sites_medication_statuses
        : [];
    const projectSitesClientOptions = participant.project_sites_client_options || null;
    const notes = Array.isArray(participant.notes) ? participant.notes : [];
    const activationHistory = Array.isArray(participant.activation_history) ? participant.activation_history : [];
    const staffHistory = Array.isArray(participant.history_ids) ? participant.history_ids : [];
    const hasFullAccess = Boolean(bootstrap.hasSuperAccess || bootstrap.isAdminUser);
    const participantProgram = normalizeValue(participant.program);
    const isManagedProgram = PROGRAM_LEVEL_MANAGEMENT_PROGRAMS.includes(participantProgram);
    const isNurselessProgram = NURSELESS_PROGRAMS.includes(participantProgram);
    const isProjectSitesClient = isProjectSitesProgram(participantProgram);
    const isMedicationReadOnlyProgram = isMedicationScatteredErcProgram(participantProgram);
    const canManageParticipant = hasFullAccess;
    const canManageMedications = canManageParticipant;
    const canEditMedicationTimelineStatus = userCanEditMedcallTimelineStatus(routeBase, Boolean(bootstrap.hasMedcallPowerAccess), canManageMedications);
    const canEditMedicationTimelineWitness = userCanEditMedcallTimelineWitness(routeBase, canManageMedications);
    const canEditMedicationTimelineSite = userCanEditMedcallTimelineSite(routeBase, Boolean(bootstrap.hasMedcallPowerAccess), canManageMedications);
    const profileProgramUsesFacility = participantProgramUsesFacility(getMedcallProfileFacilityProgram(routeBase, participantProgram, normalizeValue(profileDraft.program)));
    const showAliasEntryFields = shouldShowMedcallAliasEntryFields(routeBase, hasFullAccess);
    const identityDialogCopy = getMedcallIdentityDialogCopy(routeBase, pendingAction);
    const frequencyOptions = getMedicationFrequencyOptions(medicationDraft.route);
    const scheduleOptions = getMedicationScheduleOptions(medicationDraft.frequency);
    const projectSitesWeekStart = Number.isInteger(Number(projectSitesClientOptions?.week_start))
        ? Number(projectSitesClientOptions?.week_start)
        : 1;
    const projectSitesStatusLookup = new Map(projectSitesMedicationStatuses
        .map((status) => {
        const dateKey = normalizeValue(status.date);
        if (!dateKey) {
            return null;
        }
        return [dateKey, status] as const;
    })
        .filter(Boolean) as Array<readonly [
        string,
        MedcallProjectSitesMedicationStatus
    ]>);
    const medicationConfirmationRows = [];
    const showLegacyMedicationSearchOnly = false;
    const showLegacyMedicationDoseOnly = false;
    const activeLegacyMedicationSignatures = new Set(medications
        .filter((medication) => Boolean(medication.active))
        .map(legacyMedicationSignature));
    const legacyNonPrnMedications = medications
        .filter((medication) => !(Boolean(medication.prn) && Boolean(medication.active)))
        .filter((medication) => !(!Boolean(medication.active)
        && !legacyMedicationHasStopDate(medication)
        && activeLegacyMedicationSignatures.has(legacyMedicationSignature(medication))))
        .sort((left, right) => Number(Boolean(left.prn)) - Number(Boolean(right.prn)))
        .sort((left, right) => Number(Boolean(right.active)) - Number(Boolean(left.active)));
    const legacyPrnMedications = medications.filter((medication) => (Boolean(medication.prn) && Boolean(medication.active)));
    const visibleLegacyNonPrnMedications = medicationView === 'historical'
        ? legacyNonPrnMedications.filter((medication) => !Boolean(medication.active))
        : medicationView === 'active'
            ? legacyNonPrnMedications.filter((medication) => Boolean(medication.active))
            : legacyNonPrnMedications;
    const filteredMedications = (() => {
        ;
        if (medicationView === 'prn') {
            return medications.filter((medication) => Boolean(medication.prn) && Boolean(medication.active));
        }
        if (medicationView === 'historical') {
            return medications.filter((medication) => !Boolean(medication.prn) && !Boolean(medication.active));
        }
        return medications.filter((medication) => !Boolean(medication.prn) && Boolean(medication.active));
    })();
    const projectSitesDayGridDates = getProjectSitesMonthGridDates(projectSitesTrackerMonth, projectSitesWeekStart);
    const projectSitesWeekRanges = getProjectSitesMonthWeekRanges(projectSitesTrackerMonth, projectSitesWeekStart);
    const projectSitesMonthRanges = getProjectSitesYearMonths(projectSitesTrackerMonth);
    const projectSitesWeekdayLabels = (() => {
        const monthDate = parseUtcDateOnly(`${projectSitesTrackerMonth}-01`) || parseUtcDateOnly(`${getLosAngelesDateOnly().slice(0, 7)}-01`);
        if (!monthDate) {
            return ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
        }
        const weekStartDate = getUtcWeekStart(monthDate, projectSitesWeekStart);
        return Array.from({ length: 7 }, (_, index) => {
            const dayDate = addUtcDays(weekStartDate, index);
            return new Intl.DateTimeFormat('en-US', {
                timeZone: 'UTC',
                weekday: 'short',
            }).format(dayDate);
        });
    })();
    const availableStaffOptions = hasFullAccess
        ? staffOptions
        : staffOptions.filter((option) => option.id === participant.user_id || option.id === bootstrap.medcallUserId);
    const minDeactivationDate = getLosAngelesDateOnlyWithOffset(-7);
    const maxDeactivationDate = getLosAngelesDateOnly();
    const loadStrengthOptionsForMedication = async (externalId: string, medicineName = '', sortForEdit = false) => {
        const normalizedExternalId = normalizeValue(externalId);
        setMedicationStrengthOptions([]);
        setUsesCustomMedicationStrength(false);
        if (!normalizedExternalId) {
            return;
        }
        try {
            const result = await loadMedicationStrengths({
                variables: {
                    externalId: normalizedExternalId,
                    medicineName: normalizeValue(medicineName) || null,
                },
            });
            const strengthOptions = result.data?.medcallMedicationStrengths;
            const availableOptions = Array.isArray(strengthOptions)
                ? strengthOptions.filter((option) => Boolean(getMedcallStrengthOptionValue(option)))
                : [];
            setMedicationStrengthOptions(sortForEdit ? sortMedcallStrengthOptionsNumerically(availableOptions) : availableOptions);
        }
        catch (strengthError: any) {
            toast.error(strengthError?.message || 'Unable to load medication doses.');
        }
    };
    const handleMedicationCatalogSelection = async (item: MedcallMedicationSearchItem) => {
        const medicineName = normalizeValue(item.name || item.displayName);
        const externalId = normalizeValue(item.externalId || item.drugbankPcid || item.uuid);
        if (!medicineName) {
            return;
        }
        setLegacyMedicationSelected(true);
        setMedicationDraft((current) => ({
            ...current,
            medicationExternalId: externalId,
            medicineDetail: normalizeValue(item.medicineDetail),
            medicineName,
            strength: '',
        }));
        setMedicationSearchResults([]);
        await loadStrengthOptionsForMedication(externalId, medicineName);
    };
    const openEditDialog = () => {
        setProfileDraft(buildProfileDraft(participant));
        setActiveDialog('edit');
    };
    const openStaffDialog = () => {
        setStaffDraft(participant.user_id ? `${participant.user_id}` : '');
        setStaffDialogTab('form');
        setActiveDialog('staff');
    };
    const openNurseDialog = () => {
        setNurseDraft(buildPersonDraft(participant.nurse_name));
        setActiveDialog('nurse');
    };
    const openPsychiatristDialog = () => {
        setPsychiatristDraft(buildPersonDraft(participant.psychiatrist));
        setActiveDialog('psychiatrist');
    };
    const openNotesDialog = () => {
        setNoteDraft('');
        setActiveDialog('notes');
    };
    const openAliasesDialog = () => {
        setAliasDraft({ firstname: '', lastname: '' });
        setActiveDialog('aliases');
    };
    const openMessageDialog = () => {
        setActiveDialog('message');
    };
    const openCreateMedicationDialog = () => {
        setEditingMedicationId(null);
        setMedicationPreviewVariables(null);
        setMedicationSearchResults([]);
        setMedicationStrengthOptions([]);
        setUsesCustomMedicationStrength(false);
        setLegacyMedicationSelected(false);
        setMedicationDraft({
            ...MEDICATION_EMPTY_DRAFT,
            refillStatus: !isMedicationReadOnlyProgram,
        });
        setActiveDialog('medication');
    };
    const openEditMedicationDialog = (medication: MedcallParticipantMedication) => {
        setEditingMedicationId(medication.id);
        setMedicationPreviewVariables(null);
        const nextDraft = buildMedicationDraft(medication);
        setMedicationSearchResults([]);
        setMedicationStrengthOptions([]);
        setUsesCustomMedicationStrength(false);
        setLegacyMedicationSelected(true);
        setMedicationDraft(nextDraft);
        setActiveDialog('medication');
        void loadStrengthOptionsForMedication(nextDraft.medicationExternalId, nextDraft.medicineName, true);
    };
    const openStatusDialog = () => {
        ;
        setStatusDraft(buildStatusDraft());
        setActiveDialog('status');
    };
    const closeDialog = () => {
        setActiveDialog(null);
        setEditingMedicationId(null);
        setMedicationPreviewVariables(null);
        setDeleteNoteTarget(null);
        setMedicationSearchResults([]);
        setMedicationStrengthOptions([]);
        setMedicationSearchLoading(false);
        setUsesCustomMedicationStrength(false);
        setLegacyMedicationSelected(false);
    };
    const handleMutationError = (mutationError: any, fallbackMessage: string) => {
        toast.error(mutationError?.message || fallbackMessage);
    };
    const handleUpdateImlaiDueDate = async (update: MedcallImlaiDueDateUpdate) => {
        setPendingAction(`imlai-due-date-${update.medicationId}`);
        try {
            const result = await updateImlaiDueDate({
                variables: {
                    dueDate: update.dueDate,
                    medicationId: update.medicationId,
                },
            });
            if (result.data?.medcallUpdateImlaiDueDate?.id) {
                toast.success('Medication due date updated.');
                await refetch();
                setPendingAction(null);
                return;
            }
            toast.error('Unable to update medication due date.');
        }
        catch (mutationError: any) {
            handleMutationError(mutationError, 'Unable to update medication due date.');
        }
        setPendingAction(null);
    };
    const handleUpdateAllImlaiDueDates = async (updates: MedcallImlaiDueDateUpdate[]) => {
        setPendingAction('imlai-due-date-all');
        try {
            const results = await Promise.all(updates.map((update) => {
                return updateImlaiDueDate({
                    variables: {
                        dueDate: update.dueDate,
                        medicationId: update.medicationId,
                    },
                });
            }));
            const everyUpdateSucceeded = results.every((result) => {
                return Boolean(result.data?.medcallUpdateImlaiDueDate?.id);
            });
            if (everyUpdateSucceeded) {
                toast.success('Medication due dates updated.');
                await refetch();
                setPendingAction(null);
                return;
            }
            toast.error('Unable to update every medication due date.');
        }
        catch (mutationError: any) {
            handleMutationError(mutationError, 'Unable to update every medication due date.');
        }
        await refetch();
        setPendingAction(null);
    };
    const handleAddToMyClients = async () => {
        setPendingAction('add-list');
        try {
            const result = await addToMyClients({
                variables: {
                    participantId: activeParticipantId,
                },
            });
            const response = result.data?.medcallAddToMyClients;
            if (response?.responseCode === 200) {
                toast.success(response.message || 'Client added');
                await refetch();
                setPendingAction(null);
                return;
            }
            toast.error(response?.message || 'Unable to add client');
        }
        catch (mutationError: any) {
            handleMutationError(mutationError, 'Unable to add client');
        }
        setPendingAction(null);
    };
    const handleRemoveFromMyClients = async () => {
        setPendingAction('remove-list');
        try {
            const result = await removeFromMyClients({
                variables: {
                    participantId: activeParticipantId,
                },
            });
            const response = result.data?.medcallRemoveFromMyClients;
            if (response?.responseCode === 200) {
                toast.success(response.message || 'Client removed');
                await refetch();
                setPendingAction(null);
                return;
            }
            toast.error(response?.message || 'Unable to remove client');
        }
        catch (mutationError: any) {
            handleMutationError(mutationError, 'Unable to remove client');
        }
        setPendingAction(null);
    };
    const handleSaveProfile = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const nextProgram = normalizeValue(profileDraft.program) || participantProgram;
        const nextProvider = normalizeValue(profileDraft.provider);
        const nextDob = normalizeValue(profileDraft.dob);
        const nextCid = normalizeValue(profileDraft.cid);
        if (profileProgramUsesFacility && !normalizeValue(profileDraft.optionFacilityId)) {
            toast.error('Facility is required.');
            return;
        }
        if (hasFullAccess && !nextDob) {
            toast.error('Date of birth is required.');
            return;
        }
        if (hasFullAccess && !nextCid) {
            toast.error('CID is required.');
            return;
        }
        setPendingAction('profile');
        try {
            const variables = buildMedcallProfileMutationVariables({
                draft: {
                    cid: nextCid,
                    dob: nextDob,
                    optionFacilityId: profileDraft.optionFacilityId,
                    program: nextProgram,
                    provider: nextProvider,
                },
                hasFullAccess,
                participantId: activeParticipantId,
                programUsesFacility: profileProgramUsesFacility,
                routeBase,
            });
            const result = await updateParticipantProfile({ variables });
            if (result.data?.medcallUpdateParticipantProfile?.id) {
                toast.success('Client updated.');
                await refetch();
                setPendingAction(null);
                closeDialog();
                return;
            }
            toast.error('Unable to update client.');
        }
        catch (mutationError: any) {
            handleMutationError(mutationError, 'Unable to update client.');
        }
        setPendingAction(null);
    };
    const handleSaveStaffAssignment = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const nextStaffId = Number(staffDraft);
        ;
        if (!Number.isInteger(nextStaffId) || nextStaffId < 1) {
            toast.error('Select a staff member.');
            return;
        }
        setPendingAction('staff');
        try {
            const result = await updateParticipantStaff({
                variables: {
                    newStaffId: nextStaffId,
                    participantId: activeParticipantId,
                },
            });
            if (result.data?.medcallUpdateParticipantStaff?.id) {
                toast.success('Staff assignment updated.');
                await refetch();
                setPendingAction(null);
                closeDialog();
                return;
            }
            toast.error('Unable to update staff assignment.');
        }
        catch (mutationError: any) {
            handleMutationError(mutationError, 'Unable to update staff assignment.');
        }
        setPendingAction(null);
    };
    const saveLegacyPersonAssignment = async (kind: 'nurse' | 'psychiatrist', values: Medcall2PersonAssignmentSubmission) => {
        setPendingAction(kind);
        try {
            const variables = { ...values, participantId: activeParticipantId };
            const result = kind === 'nurse'
                ? await updateParticipantNurse({ variables })
                : await updateParticipantPsychiatrist({ variables });
            const record = result.data?.medcallUpdateParticipantNurse || result.data?.medcallUpdateParticipantPsychiatrist;
            if (!record?.id) {
                toast.error('Unable to update assignment.');
                return;
            }
            await refetch();
            setPendingAction(null);
            closeDialog();
        }
        catch (error) {
            handleMutationError(error, 'Unable to update assignment.');
        }
        finally {
            setPendingAction(null);
        }
    };
    const handleSaveNurse = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const firstname = normalizeValue(nurseDraft.firstname);
        const lastname = normalizeValue(nurseDraft.lastname);
        const title = normalizeValue(nurseDraft.title);
        const email = normalizeEmail(nurseDraft.email);
        const confirmEmail = normalizeEmail(nurseDraft.confirmEmail);
        if (!firstname || !lastname || !title || !email) {
            toast.error('First name, last name, title, and email are required.');
            return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            toast.error('Enter a valid email address.');
            return;
        }
        if (isBlockedEmailDomain(email, filterDomains)) {
            toast.error('Use an agency email address.');
            return;
        }
        if (confirmEmail !== email) {
            toast.error('The email addresses do not match.');
            return;
        }
        setPendingAction('nurse');
        try {
            const result = await updateParticipantNurse({
                variables: {
                    email,
                    firstname,
                    lastname,
                    participantId: activeParticipantId,
                    title,
                },
            });
            if (result.data?.medcallUpdateParticipantNurse?.id) {
                toast.success('Nurse updated.');
                await refetch();
                setPendingAction(null);
                closeDialog();
                return;
            }
            toast.error('Unable to update nurse.');
        }
        catch (mutationError: any) {
            handleMutationError(mutationError, 'Unable to update nurse.');
        }
        setPendingAction(null);
    };
    const handleSavePsychiatrist = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const firstname = normalizeValue(psychiatristDraft.firstname);
        const lastname = normalizeValue(psychiatristDraft.lastname);
        const title = normalizeValue(psychiatristDraft.title);
        const email = normalizeEmail(psychiatristDraft.email);
        const confirmEmail = normalizeEmail(psychiatristDraft.confirmEmail);
        if (!firstname || !lastname || !title) {
            toast.error('First name, last name, and title are required.');
            return;
        }
        if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            toast.error('Enter a valid email address.');
            return;
        }
        if (email && isBlockedEmailDomain(email, filterDomains)) {
            toast.error('Use an agency email address.');
            return;
        }
        if (email && confirmEmail !== email) {
            toast.error('The email addresses do not match.');
            return;
        }
        setPendingAction('psychiatrist');
        try {
            const result = await updateParticipantPsychiatrist({
                variables: {
                    email: email || null,
                    firstname,
                    lastname,
                    participantId: activeParticipantId,
                    title,
                },
            });
            if (result.data?.medcallUpdateParticipantPsychiatrist?.id) {
                toast.success('Psychiatrist updated.');
                await refetch();
                setPendingAction(null);
                closeDialog();
                return;
            }
            toast.error('Unable to update psychiatrist.');
        }
        catch (mutationError: any) {
            handleMutationError(mutationError, 'Unable to update psychiatrist.');
        }
        setPendingAction(null);
    };
    const handleAddNote = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const noteText = normalizeValue(noteDraft);
        if (!noteText) {
            toast.error('Enter a note before saving.');
            return;
        }
        setPendingAction('note');
        try {
            const result = await addParticipantNote({
                variables: {
                    note: noteText,
                    participantId: activeParticipantId,
                },
            });
            if (result.data?.medcallAddParticipantNote?.id) {
                toast.success('Note added.');
                setNoteDraft('');
                await refetch();
                setPendingAction(null);
                return;
            }
            toast.error('Unable to save note.');
        }
        catch (mutationError: any) {
            handleMutationError(mutationError, 'Unable to save note.');
        }
        setPendingAction(null);
    };
    const handleAddAlias = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        ;
        const firstname = normalizeValue(aliasDraft.firstname);
        const lastname = normalizeValue(aliasDraft.lastname);
        if (!firstname) {
            toast.error('Firstname is required.');
            return;
        }
        if (!lastname) {
            toast.error('Lastname is required.');
            return;
        }
        setPendingAction('alias');
        try {
            const result = await addParticipantAlias({
                variables: {
                    firstname,
                    lastname,
                    participantId: activeParticipantId,
                },
            });
            if (result.data?.medcallAddParticipantAlias?.id) {
                toast.success('Alias added.');
                await refetch();
                setPendingAction(null);
                closeDialog();
                return;
            }
            toast.error('Unable to add alias.');
        }
        catch (mutationError: any) {
            handleMutationError(mutationError, 'Unable to add alias.');
        }
        setPendingAction(null);
    };
    const handleDeleteNote = async (noteId = deleteNoteTarget?.id) => {
        if (!noteId) {
            return;
        }
        setPendingAction(`delete-note-${noteId}`);
        try {
            const result = await deleteParticipantNote({
                variables: {
                    noteId,
                    participantId: activeParticipantId,
                },
            });
            if (result.data?.medcallDeleteParticipantNote?.responseCode === 200) {
                toast.success('Note deleted.');
                await refetch();
                setPendingAction(null);
                setDeleteNoteTarget(null);
                return;
            }
            toast.error('Unable to delete note.');
        }
        catch (mutationError: any) {
            handleMutationError(mutationError, 'Unable to delete note.');
        }
        setPendingAction(null);
    };
    const handleSaveStatus = async (event?: React.FormEvent<HTMLFormElement>) => {
        event?.preventDefault();
        const nextActive = !Boolean(participant.active);
        let resolvedReason = '';
        if (!nextActive) {
            if (!statusDraft.reason) {
                toast.error('Select a reason for deactivation.');
                return;
            }
            resolvedReason = statusDraft.reason === 'Other'
                ? `Other - ${normalizeValue(statusDraft.otherReason)}`
                : statusDraft.reason;
            if (statusDraft.reason === 'Other' && !normalizeValue(statusDraft.otherReason)) {
                toast.error('Enter a reason for deactivation.');
                return;
            }
            if (!statusDraft.activationBackDate) {
                toast.error('Select a deactivation date.');
                return;
            }
            if (statusDraft.activationBackDate < minDeactivationDate || statusDraft.activationBackDate > maxDeactivationDate) {
                toast.error('Choose a deactivation date within the last 7 days.');
                return;
            }
        }
        setPendingAction('status');
        try {
            const variables: Record<string, any> = {
                active: nextActive,
                participantId: activeParticipantId,
            };
            if (!nextActive) {
                variables.activationBackDate = statusDraft.activationBackDate;
                variables.deactivationReason = resolvedReason;
            }
            const result = await setParticipantActive({ variables });
            if (result.data?.medcallSetParticipantActive?.id) {
                toast.success(nextActive ? 'Client activated.' : 'Client deactivated.');
                await refetch();
                setPendingAction(null);
                closeDialog();
                return;
            }
            toast.error('Unable to update client status.');
        }
        catch (mutationError: any) {
            handleMutationError(mutationError, 'Unable to update client status.');
        }
        setPendingAction(null);
    };
    const handleMedicationRouteChange = (value: string) => {
        const nextFrequencyOptions = getMedicationFrequencyOptions(value);
        const nextFrequency = nextFrequencyOptions.some((option) => option.value === medicationDraft.frequency)
            ? medicationDraft.frequency
            : '';
        const nextScheduleOptions = getMedicationScheduleOptions(nextFrequency);
        setMedicationDraft((current) => ({
            ...current,
            administrationNotes: value === 'IM-LAI' ? current.administrationNotes : '',
            dateDue: value === 'IM-LAI' ? current.dateDue : '',
            dateGiven: value === 'IM-LAI' ? current.dateGiven : '',
            dateInjectionArrived: value === 'IM-LAI' ? current.dateInjectionArrived : '',
            frequency: nextFrequency,
            importanceLevel: value === 'IM-LAI' ? '2' : current.importanceLevel,
            onSite: value === 'IM-LAI' ? current.onSite : '0',
            prn: value === 'IM-LAI' ? false : current.prn,
            psychotropicMeds: value === 'IM-LAI' ? false : current.psychotropicMeds,
            refillDate: value === 'IM-LAI' ? '' : current.refillDate,
            refillStatus: value === 'IM-LAI' ? false : current.refillStatus || !isMedicationReadOnlyProgram,
            route: value,
            startDate: value === 'IM-LAI' ? '' : current.startDate,
            stopDate: value === 'IM-LAI' ? '' : current.stopDate,
            stopDateEnabled: value === 'IM-LAI' ? false : current.stopDateEnabled,
            timesPerDay: nextScheduleOptions.includes(current.timesPerDay)
                ? current.timesPerDay
                : (nextScheduleOptions[0] || (value === 'IM-LAI' ? 'qday' : '')),
        }));
    };
    const handleMedicationFrequencyChange = (value: string) => {
        const nextScheduleOptions = getMedicationScheduleOptions(value);
        setMedicationDraft((current) => ({
            ...current,
            customDays: value === 'custom_days' ? current.customDays : [],
            frequency: value,
            timesPerDay: nextScheduleOptions.includes(current.timesPerDay)
                ? current.timesPerDay
                : (nextScheduleOptions[0] || ''),
        }));
    };
    const handleMedicationDayToggle = (dayValue: string, checked: boolean) => {
        setMedicationDraft((current) => {
            const currentDays = new Set(current.customDays);
            if (checked) {
                currentDays.add(dayValue);
            }
            if (!checked) {
                currentDays.delete(dayValue);
            }
            return {
                ...current,
                customDays: Array.from(currentDays),
            };
        });
    };
    const executeMedicationSave = async (variables: Record<string, any>) => {
        setPendingAction(editingMedicationId ? 'medication-update' : 'medication-create');
        try {
            const result = editingMedicationId
                ? await updateMedication({
                    variables: {
                        ...variables,
                        medicationId: editingMedicationId,
                    },
                })
                : await createMedication({ variables });
            const response = editingMedicationId
                ? result.data?.medcallUpdateMedication
                : result.data?.medcallCreateMedication;
            if (response?.id) {
                toast.success(editingMedicationId ? 'Medication updated.' : 'Medication added.');
                await refetch();
                setPendingAction(null);
                closeDialog();
                return;
            }
            toast.error(editingMedicationId ? 'Unable to update medication.' : 'Unable to add medication.');
        }
        catch (mutationError: any) {
            handleMutationError(mutationError, editingMedicationId ? 'Unable to update medication.' : 'Unable to add medication.');
        }
        setPendingAction(null);
    };
    const handleConfirmMedicationSave = async () => {
        if (!medicationPreviewVariables || medicationPreviewSubmittingRef.current) {
            return;
        }
        medicationPreviewSubmittingRef.current = true;
        try {
            await executeMedicationSave(medicationPreviewVariables);
        }
        finally {
            medicationPreviewSubmittingRef.current = false;
        }
    };
    const handleSaveMedication = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const medicineName = normalizeValue(medicationDraft.medicineName);
        const strength = normalizeValue(medicationDraft.strength);
        const route = normalizeValue(medicationDraft.route);
        const frequency = normalizeValue(medicationDraft.frequency).toLowerCase();
        const timesPerDay = normalizeValue(medicationDraft.timesPerDay).toLowerCase();
        if (!medicineName || !strength || !route || !frequency || !timesPerDay) {
            toast.error('Medication name, dose, route, frequency, and schedule are required.');
            return;
        }
        if (frequency === 'custom_days' && medicationDraft.customDays.length < 1) {
            toast.error('Select at least one custom day.');
            return;
        }
        if (route !== 'IM-LAI' && !medicationDraft.startDate) {
            toast.error('Start date and time are required.');
            return;
        }
        if (route !== 'IM-LAI' && medicationDraft.stopDateEnabled && !medicationDraft.stopDate) {
            toast.error('Enter the discontinue date and time.');
            return;
        }
        if (route !== 'IM-LAI' && !isMedicationReadOnlyProgram && medicationDraft.refillStatus && !medicationDraft.refillDate) {
            toast.error('Enter the runs out date.');
            return;
        }
        if (route === 'IM-LAI' && !medicationDraft.dateDue) {
            toast.error('Enter the injection due date.');
            return;
        }
        if (route === 'IM-LAI' && medicationDraft.onSite === '2' && !medicationDraft.dateInjectionArrived) {
            toast.error('Enter the date the injection arrived on-site.');
            return;
        }
        if (route === 'IM-LAI' && medicationDraft.dateGiven && !normalizeValue(medicationDraft.administrationNotes)) {
            toast.error('Enter the injection site location.');
            return;
        }
        const variables: Record<string, any> = {
            administrationNotes: normalizeValue(medicationDraft.administrationNotes) || null,
            daysOfWeek: frequency === 'custom_days' ? medicationDraft.customDays : [],
            frequency,
            importanceLevel: route === 'IM-LAI' ? 2 : Number(medicationDraft.importanceLevel || 2),
            injectionDateArrived: route === 'IM-LAI' && medicationDraft.onSite === '2'
                ? (medicationDraft.dateInjectionArrived || null)
                : null,
            injectionDateDue: route === 'IM-LAI' ? (medicationDraft.dateDue || null) : null,
            injectionDateGiven: route === 'IM-LAI' ? (medicationDraft.dateGiven || null) : null,
            injectionOnSite: route === 'IM-LAI' ? Number(medicationDraft.onSite || 0) : 0,
            medicineDetail: normalizeValue(medicationDraft.medicineDetail) || null,
            medicineName,
            participantId: activeParticipantId,
            prn: route === 'IM-LAI' ? false : medicationDraft.prn,
            psychotropicMeds: route === 'IM-LAI' ? false : medicationDraft.psychotropicMeds,
            refillDate: route === 'IM-LAI' || isMedicationReadOnlyProgram || !medicationDraft.refillStatus
                ? null
                : (medicationDraft.refillDate || null),
            refillStatus: route === 'IM-LAI' || isMedicationReadOnlyProgram ? false : medicationDraft.refillStatus,
            route,
            startDate: route === 'IM-LAI' ? null : (medicationDraft.startDate || null),
            stopDate: route === 'IM-LAI' || !medicationDraft.stopDateEnabled ? null : (medicationDraft.stopDate || null),
            stopDateStatus: route === 'IM-LAI' ? false : medicationDraft.stopDateEnabled,
            strength,
            timesPerDay,
        };
        ;
        await executeMedicationSave(variables);
    };
    const handleToggleMedicationStatus = async (medication: MedcallParticipantMedication) => {
        const nextActive = !Boolean(medication.active);
        setPendingAction(`medication-status-${medication.id}`);
        try {
            const result = await setMedicationActive({
                variables: {
                    active: nextActive,
                    medicationId: medication.id,
                },
            });
            if (result.data?.medcallSetMedicationActive?.id) {
                toast.success(nextActive ? 'Medication activated.' : 'Medication discontinued.');
                await refetch();
                setPendingAction(null);
                return;
            }
            toast.error('Unable to update medication.');
        }
        catch (mutationError: any) {
            handleMutationError(mutationError, 'Unable to update medication.');
        }
        setPendingAction(null);
    };
    const handleDeleteMedication = async (medication: MedcallParticipantMedication) => {
        if (Boolean(medication.active)) {
            toast.error('Discontinue the medication before deleting it.');
            return;
        }
        if (!window.confirm(`Delete ${normalizeValue(medication.medicine_name) || 'this medication'}?`)) {
            return;
        }
        setPendingAction(`medication-delete-${medication.id}`);
        try {
            const result = await deleteMedication({
                variables: {
                    medicationId: medication.id,
                },
            });
            const response = result.data?.medcallDeleteMedication;
            if (response?.responseCode === 200) {
                toast.success(response.message || 'Medication deleted.');
                await refetch();
                setPendingAction(null);
                return;
            }
            toast.error(response?.message || 'Unable to delete medication.');
        }
        catch (mutationError: any) {
            handleMutationError(mutationError, 'Unable to delete medication.');
        }
        setPendingAction(null);
    };
    const handleSaveMedicationTimelineStatus = async (timeline: MedcallMedicationTimeline, nextStatus: 'notTaken' | 'taken' | 'unset') => {
        setPendingAction(`timeline-status-${timeline.id}`);
        try {
            const result = await updateMedicationTimeline({
                variables: {
                    preserveLegacySemantics: false,
                    status: nextStatus,
                    time: undefined,
                    timelineId: timeline.id,
                },
            });
            if (result.data?.medcallUpdateMedicationTimeline?.id
                || didMedcall2TimelineMutationComplete(routeBase, result.data, 'medcallUpdateMedicationTimeline')) {
                ;
                toast.success('Medication timeline updated.');
                await refetch();
                setPendingAction(null);
                return;
            }
            toast.error('Unable to update medication timeline.');
        }
        catch (mutationError: any) {
            handleMutationError(mutationError, 'Unable to update medication timeline.');
        }
        setPendingAction(null);
    };
    const handleSaveMedicationTimelineWitness = async (timeline: MedcallMedicationTimeline, witnessedByOther: boolean) => {
        setPendingAction(`timeline-witness-${timeline.id}`);
        try {
            const result = await updateMedicationTimeline({
                variables: {
                    preserveLegacySemantics: false,
                    timelineId: timeline.id,
                    witnessedByOther,
                },
            });
            if (result.data?.medcallUpdateMedicationTimeline?.id
                || didMedcall2TimelineMutationComplete(routeBase, result.data, 'medcallUpdateMedicationTimeline')) {
                ;
                toast.success('Witness status updated.');
                await refetch();
                setPendingAction(null);
                return;
            }
            toast.error('Unable to update witness status.');
        }
        catch (mutationError: any) {
            handleMutationError(mutationError, 'Unable to update witness status.');
        }
        setPendingAction(null);
    };
    const handleSaveMedicationTimelineSite = async (timeline: MedcallMedicationTimeline, site: string) => {
        setPendingAction(`timeline-site-${timeline.id}`);
        try {
            const result = await updateMedicationTimelineSite({
                variables: {
                    preserveLegacySemantics: false,
                    site: site || null,
                    timelineId: timeline.id,
                },
            });
            if (result.data?.medcallUpdateMedicationTimelineSite?.id
                || didMedcall2TimelineMutationComplete(routeBase, result.data, 'medcallUpdateMedicationTimelineSite')) {
                ;
                toast.success('Medication site updated.');
                await refetch();
                setPendingAction(null);
                return;
            }
            toast.error('Unable to update medication site.');
        }
        catch (mutationError: any) {
            handleMutationError(mutationError, 'Unable to update medication site.');
        }
        setPendingAction(null);
    };
    const handleSaveMedicationAdministrationNotes = async (timeline: MedcallMedicationTimeline, administrationNotes: string) => {
        if (!false) {
            setPendingAction(`timeline-administration-notes-${timeline.id}`);
        }
        try {
            const result = await updateMedicationAdministrationNotes({
                variables: {
                    administrationNotes,
                    preserveLegacySemantics: false,
                    timelineId: timeline.id,
                },
            });
            if (result.data?.medcallUpdateMedicationAdministrationNotes?.id) {
                ;
                await refetch();
                setPendingAction(null);
                return true;
            }
        }
        catch (_mutationError) {
            if (!false) {
                setPendingAction(null);
            }
            return false;
        }
        if (!false) {
            setPendingAction(null);
        }
        return false;
    };
    const openProjectSitesTrackerSelection = (selection: ProjectSitesTrackerSelection) => {
        const currentStatus = projectSitesStatusLookup.get(selection.startDate);
        setProjectSitesTrackerSelection(selection);
        setProjectSitesTrackerStatus(currentStatus?.status === 'taken' ? 'taken' : 'notTaken');
        setProjectSitesTrackerNotes(normalizeValue(currentStatus?.notes));
    };
    const handleSaveProjectSitesTrackerSelection = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!projectSitesTrackerSelection) {
            return;
        }
        setPendingAction('project-sites-tracker');
        try {
            const result = await updateProjectSitesMedicationStatuses({
                variables: {
                    endDate: projectSitesTrackerSelection.endDate,
                    notes: normalizeValue(projectSitesTrackerNotes) || null,
                    participantId: activeParticipantId,
                    startDate: projectSitesTrackerSelection.startDate,
                    status: projectSitesTrackerStatus,
                },
            });
            if (Array.isArray(result.data?.medcallUpdateProjectSitesMedicationStatuses)) {
                toast.success('Tracker updated.');
                await refetch();
                setPendingAction(null);
                setProjectSitesTrackerSelection(null);
                return;
            }
            toast.error('Unable to update tracker.');
        }
        catch (mutationError: any) {
            handleMutationError(mutationError, 'Unable to update tracker.');
        }
        setPendingAction(null);
    };
    const handleSaveProjectSitesWeekStart = async (nextWeekStart: string) => {
        setProjectSitesWeekStartDraft(nextWeekStart);
        setPendingAction('project-sites-options');
        try {
            const result = await updateProjectSitesClientOptions({
                variables: {
                    participantId: activeParticipantId,
                    weekStart: Number(nextWeekStart),
                },
            });
            if (result.data?.medcallUpdateProjectSitesClientOptions?.id) {
                toast.success('Tracker settings updated.');
                await refetch();
                setPendingAction(null);
                return;
            }
            toast.error('Unable to update tracker settings.');
        }
        catch (mutationError: any) {
            handleMutationError(mutationError, 'Unable to update tracker settings.');
        }
        setPendingAction(null);
    };
    const participantActions = (<MedcallParticipantActions canManageMedications={canManageMedications} canManageParticipant={canManageParticipant} isNurselessProgram={isNurselessProgram} isOnMyClients={isOnMyClients} onAddMedication={openCreateMedicationDialog} onAddToMyClients={handleAddToMyClients} onAssignNurse={openNurseDialog} onAssignPsychiatrist={openPsychiatristDialog} onAssignStaff={openStaffDialog} onClientAliases={openAliasesDialog} onClientNotes={openNotesDialog} onEditClient={openEditDialog} onMedcallMessage={openMessageDialog} onMedicationList={() => setActiveDialog('med-list')} onParticipantStatus={openStatusDialog} onRemoveFromMyClients={handleRemoveFromMyClients} onReport={() => setActiveDialog('report')} participantActive={Boolean(participant.active)} pending={pendingAction !== null} renderParticipantStatusAction={undefined}/>);
    const psychiatristLabel = formatPersonLabel(participant.psychiatrist);
    const nurseLabel = formatPersonLabel(participant.nurse_name);
    return (<div className="space-y-4">
      {!embedded && !false ? (<Button className="h-8 gap-2" id="medcall-workspace-participant-back" onClick={() => navigate(backPath)} size="sm" variant="ghost">
          Back to List
        </Button>) : null}

      {(<Card>
          <CardHeader className="gap-4">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <CardTitle className="text-2xl">
                    {normalizeValue(participant.firstname)} {normalizeValue(participant.lastname)}
                  </CardTitle>
                  <Badge style={getActiveBadgeStyle(Boolean(participant.active))} variant="outline">
                    {participant.active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </div>
              {participantActions}
            </div>
          </CardHeader>
          <CardContent className={`grid gap-x-3 gap-y-2 pt-2 ${embedded ? 'grid-cols-7' : 'sm:grid-cols-2 xl:grid-cols-4'}`}>
            <DetailField compact={embedded} label="Aliases" value={formatAliases(participant.aliases)}/>
            <DetailField compact={embedded} label="Program" value={participantProgram || '—'}/>
            <DetailField compact={embedded} label="Date Added" value={formatDateTime(participant.created_at)}/>
            {!embedded ? <DetailField label="DOB" value={formatDate(participant.dob)}/> : null}
            {!embedded ? <DetailField label="CID" value={normalizeValue(participant.cid) || '—'}/> : null}
            {!embedded ? <DetailField label="Provider" value={normalizeValue(participant.provider) || '—'}/> : null}
            {!isNurselessProgram ? (<DetailField compact={embedded} label="Facility" value={normalizeValue(participant.facility) || '—'}/>) : null}
            <DetailField compact={embedded} label="Psychiatrist" value={formatPersonLabel(participant.psychiatrist)}/>
            <DetailField compact={embedded} label="Assigned Staff" value={normalizeValue(participant.staff_assigned) || '—'}/>
            {!isNurselessProgram ? (<DetailField compact={embedded} label="Nurse" value={formatPersonLabel(participant.nurse_name)}/>) : null}
          </CardContent>
        </Card>)}

      {isProjectSitesClient ? (<Card>
          <CardHeader className="gap-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <CardTitle>Medication Tracker</CardTitle>
                <CardDescription>Track daily medication status for this client.</CardDescription>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Tabs onValueChange={(value) => setProjectSitesTrackerView(value as ProjectSitesTrackerView)} value={projectSitesTrackerView}>
                  <TabsList className="h-auto rounded-[var(--border-radius-medium)] bg-[var(--surface-muted)] p-1">
                    {PROJECT_SITES_TRACKER_VIEW_OPTIONS.map((option) => (<TabsTrigger className="rounded-[var(--border-radius-small)] px-3 py-1.5" key={option.value} value={option.value}>
                        {option.label}
                      </TabsTrigger>))}
                  </TabsList>
                </Tabs>
                <Input className="w-[150px]" onChange={(event) => setProjectSitesTrackerMonth(event.target.value || getLosAngelesDateOnly().slice(0, 7))} type="month" value={projectSitesTrackerMonth}/>
                <Select disabled={!canManageParticipant || pendingAction !== null} onValueChange={handleSaveProjectSitesWeekStart} value={projectSitesWeekStartDraft}>
                  <SelectTrigger className="w-[190px]">
                    <SelectValue placeholder="Week Start"/>
                  </SelectTrigger>
                  <SelectContent>
                    {PROJECT_SITES_WEEK_START_OPTIONS.map((option) => (<SelectItem key={option.value} value={`${option.value}`}>
                        {option.label}
                      </SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 pt-4">
            {projectSitesTrackerView === 'day' ? (<div className="space-y-2">
                <div className="grid grid-cols-7 gap-2">
                  {projectSitesWeekdayLabels.map((label) => (<div className="px-2 text-center text-xs font-semibold uppercase tracking-wide text-[var(--neutral-gray-90)]" key={label}>
                      {label}
                    </div>))}
                </div>
                <div className="grid grid-cols-7 gap-2">
                  {projectSitesDayGridDates.map((date) => {
                    const dateKey = formatUtcDateOnly(date);
                    const selectedMonth = parseUtcDateOnly(`${projectSitesTrackerMonth}-01`);
                    const isCurrentMonth = Boolean(selectedMonth
                        && date.getUTCFullYear() === selectedMonth.getUTCFullYear()
                        && date.getUTCMonth() === selectedMonth.getUTCMonth());
                    const statusLabel = getProjectSitesStatusLabel(projectSitesStatusLookup, dateKey, dateKey);
                    const isTaken = statusLabel === 'Taken';
                    return (<button className={[
                            'min-h-[72px] rounded-[var(--border-radius-medium)] border px-2 py-2 text-left transition',
                            isCurrentMonth
                                ? isTaken
                                    ? 'border-[var(--semantic-success)] bg-[var(--semantic-success-soft)] text-[var(--semantic-success)]'
                                    : 'border-[var(--neutral-gray-30)] bg-white text-[var(--neutral-gray-120)] hover:bg-[var(--surface-muted)]'
                                : 'border-[var(--neutral-gray-20)] bg-[var(--surface-muted)] text-[var(--neutral-gray-70)]',
                        ].join(' ')} disabled={!canManageParticipant || !isCurrentMonth || pendingAction !== null} key={dateKey} onClick={() => openProjectSitesTrackerSelection({
                            endDate: null,
                            label: formatDate(dateKey),
                            startDate: dateKey,
                        })} type="button">
                        <div className="text-sm font-semibold">{date.getUTCDate()}</div>
                        <div className="mt-2 text-xs uppercase tracking-wide">{statusLabel}</div>
                      </button>);
                })}
                </div>
              </div>) : null}

            {projectSitesTrackerView === 'week' ? (<div className="grid gap-2 md:grid-cols-2">
                {projectSitesWeekRanges.map((range) => {
                    const statusLabel = getProjectSitesStatusLabel(projectSitesStatusLookup, range.startDate, range.endDate);
                    const isTaken = statusLabel === 'Taken';
                    return (<button className={[
                            'rounded-[var(--border-radius-medium)] border px-3 py-3 text-left transition',
                            isTaken
                                ? 'border-[var(--semantic-success)] bg-[var(--semantic-success-soft)] text-[var(--semantic-success)]'
                                : 'border-[var(--neutral-gray-30)] bg-white text-[var(--neutral-gray-120)] hover:bg-[var(--surface-muted)]',
                        ].join(' ')} disabled={!canManageParticipant || pendingAction !== null} key={range.startDate} onClick={() => openProjectSitesTrackerSelection(range)} type="button">
                      <div className="text-sm font-semibold">{range.label}</div>
                      <div className="mt-1 text-xs uppercase tracking-wide">{statusLabel}</div>
                    </button>);
                })}
              </div>) : null}

            {projectSitesTrackerView === 'month' ? (<div className="grid gap-2 md:grid-cols-3">
                {projectSitesMonthRanges.map((range) => {
                    const statusLabel = getProjectSitesStatusLabel(projectSitesStatusLookup, range.startDate, range.endDate);
                    const isTaken = statusLabel === 'Taken';
                    return (<button className={[
                            'rounded-[var(--border-radius-medium)] border px-3 py-3 text-left transition',
                            isTaken
                                ? 'border-[var(--semantic-success)] bg-[var(--semantic-success-soft)] text-[var(--semantic-success)]'
                                : 'border-[var(--neutral-gray-30)] bg-white text-[var(--neutral-gray-120)] hover:bg-[var(--surface-muted)]',
                        ].join(' ')} disabled={!canManageParticipant || pendingAction !== null} key={range.startDate} onClick={() => openProjectSitesTrackerSelection(range)} type="button">
                      <div className="text-sm font-semibold">{range.label}</div>
                      <div className="mt-1 text-xs uppercase tracking-wide">{statusLabel}</div>
                    </button>);
                })}
              </div>) : null}
          </CardContent>
        </Card>) : null}

      <div className={'grid gap-4 xl:grid-cols-[1.25fr_0.75fr]'}>
        <Card className={''}>
          {!false ? <CardHeader className="gap-0 px-4 py-2.5">
            <div className="flex flex-nowrap items-center justify-between gap-2">
              <div className="flex min-w-0 flex-nowrap items-center gap-1.5">
                <CardTitle>Medications</CardTitle>
                <Tabs className="shrink-0" onValueChange={(value) => setMedicationView(value as MedcallMedicationView)} value={medicationView}>
                  <TabsList className="h-6 rounded-[var(--border-radius-small)] bg-[var(--surface-muted)] p-0.5">
                    <TabsTrigger className="h-5 rounded-[var(--border-radius-small)] px-2 py-0 text-[10px]" value="active">
                      Active
                    </TabsTrigger>
                    <TabsTrigger className="h-5 rounded-[var(--border-radius-small)] px-2 py-0 text-[10px]" value="historical">
                      Historical
                    </TabsTrigger>
                    <TabsTrigger className="h-5 rounded-[var(--border-radius-small)] px-2 py-0 text-[10px]" value="prn">
                      PRN
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
              {canManageMedications ? (<div className={ACTION_LINK_GROUP_CLASS}>
                  <Button disabled={pendingAction !== null} id="medcall-medications-add-button" onClick={openCreateMedicationDialog} size="sm">
                    <Plus className="h-4 w-4"/>
                    Add Medication
                  </Button>
                </div>) : null}
            </div>
          </CardHeader> : null}
          <CardContent className={'space-y-4 pt-2'}>
            {([{ key: 'existing', medications: filteredMedications }]).map((group) => (<MedicationColumn key={group.key}>
            {null}
            {!false && filteredMedications.length < 1 ? (<div className="text-sm text-[var(--neutral-gray-100)]">No medications recorded for this view.</div>) : null}

            {group.medications.map((medication) => {
                const sourceTimelines = embedded
                    ? medicationTimelineHistory.get(medication.id) || []
                    : Array.isArray(medication.timelines) ? medication.timelines : [];
                const timelines = sourceTimelines.map((timeline) => {
                    const timelineOverride = medicationTimelineOverrides.get(timeline.id);
                    if (!timelineOverride) {
                        return timeline;
                    }
                    return { ...timeline, ...timelineOverride };
                });
                const isImOtherMedication = normalizeValue(medication.route) === 'IM-Other';
                const isImLaiMedication = normalizeValue(medication.route) === 'IM-LAI';
                const hasMedicationHistory = shouldShowMedcallMedicationHistory({
                    embedded,
                    program: participantProgram,
                    route: normalizeValue(medication.route),
                    routeBase,
                    timelineCount: timelines.length,
                });
                const isMedicationRunOut = false;
                const medicationWarning = null;
                const visibleTimelines = getVisibleMedcallTimelineRows(timelines, normalizeValue(medication.route), routeBase);
                const timelinePage = {
                    page: 1,
                    pageCount: 1,
                    rows: visibleTimelines,
                };
                const isMedicationHistoryExpanded = expandedMedicationIds.has(medication.id);
                const medicationHistoryToggleLabel = isMedicationHistoryExpanded
                    ? isImLaiMedication
                        ? 'Hide Previous Dates'
                        : 'Hide Timeline'
                    : isImLaiMedication
                        ? 'Show Previous Dates'
                        : 'Show Timeline';
                return (<div className={'rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-30)]'} key={medication.id}>
                  {(<div className={`border-b border-[var(--neutral-gray-20)] px-4 py-3${medicationWarning ? ' bg-[var(--semantic-error-soft)]' : ''}`}>
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        <div className="space-y-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <div className="text-sm font-semibold text-[var(--neutral-gray-120)]">
                              {normalizeValue(medication.medicine_name) || 'Unnamed medication'}
                            </div>
                            <Badge style={getActiveBadgeStyle(Boolean(medication.active))} variant="outline">
                              {medication.active ? 'Active' : 'Inactive'}
                            </Badge>
                            {medication.prn ? (<Badge style={warningBadgeStyle} variant="outline">PRN</Badge>) : null}
                          </div>
                          <div className="text-sm text-[var(--neutral-gray-100)]">
                            {[normalizeValue(medication.strength), normalizeValue(medication.route), formatMedicationFrequency(medication)]
                            .filter(Boolean)
                            .join(' • ') || '—'}
                          </div>
                        </div>
                        {canManageMedications ? (<div className={ACTION_LINK_GROUP_CLASS}>
                            <Button disabled={pendingAction !== null} id={`medcall-workspace-medication-${medication.id}-edit`} onClick={() => openEditMedicationDialog(medication)} size="sm" variant="outline">Edit</Button>
                            <Button disabled={pendingAction !== null} id={`medcall-workspace-medication-${medication.id}-status`} onClick={() => handleToggleMedicationStatus(medication)} size="sm" variant="outline">
                              {medication.active ? 'Discontinue' : 'Activate'}
                            </Button>
                            <Button disabled={pendingAction !== null || Boolean(medication.active)} id={`medcall-workspace-medication-${medication.id}-delete`} onClick={() => handleDeleteMedication(medication)} size="sm" variant="outline">Delete</Button>
                          </div>) : null}
                      </div>
                      <div className="mt-2 flex flex-nowrap items-center justify-between gap-2">
                        <div className="min-w-0 truncate text-xs uppercase tracking-wide text-[var(--neutral-gray-90)]">
                          Importance {formatImportanceLabel(medication.importance_level)} • Last Given {formatDate(medication.last_taken_date)}
                        </div>
                        {hasMedicationHistory ? (<Button aria-controls={`medication-history-${medication.id}`} aria-expanded={isMedicationHistoryExpanded} className="h-auto shrink-0 px-0 py-0" id={`medcall-medication-${medication.id}-timeline-button`} onClick={() => toggleMedicationHistory(medication.id)} size="sm" type="button" variant="link">
                            {isMedicationHistoryExpanded ? <ChevronUp className="h-3.5 w-3.5"/> : <ChevronDown className="h-3.5 w-3.5"/>}
                            {medicationHistoryToggleLabel}
                          </Button>) : null}
                      </div>
                    </div>)}

                  {hasMedicationHistory && isMedicationHistoryExpanded ? (<div className={'overflow-x-auto px-4 py-3'} id={`medication-history-${medication.id}`}>
                      {isImLaiMedication ? (<table className="w-full min-w-[720px] text-sm">
                          <thead>
                            <tr className="border-b border-[var(--neutral-gray-30)] text-left text-xs uppercase tracking-wide text-[var(--neutral-gray-90)]">
                              <th className="py-2 pr-3 font-semibold">Frequency</th>
                              <th className="py-2 pr-3 font-semibold">Next Due Date</th>
                              <th className="py-2 pr-3 font-semibold">Status</th>
                              <th className="py-2 pr-3 font-semibold">Date Given</th>
                              <th className="py-2 pr-0 font-semibold">
                                {'Injection Site'}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {timelinePage.rows.map((timeline) => {
                                const injectionInfo = Array.isArray(timeline.injection_info) ? timeline.injection_info[0] : null;
                                return (<tr className="border-b border-[var(--neutral-gray-20)] align-top last:border-b-0" key={timeline.id}>
                                  <td className="py-2 pr-3 text-[var(--neutral-gray-120)]">
                                    {formatMedicationFrequency(medication)}
                                  </td>
                                  <td className="py-2 pr-3 text-[var(--neutral-gray-120)]">{formatDate(injectionInfo?.date_due)}</td>
                                  <td className="py-2 pr-3 text-[var(--neutral-gray-120)]">
                                    {formatInjectionOnSiteLabel(injectionInfo?.on_site, false)}
                                  </td>
                                  <td className="py-2 pr-3 text-[var(--neutral-gray-120)]">{formatDate(injectionInfo?.date_given)}</td>
                                  <td className="py-2 pr-0 text-[var(--neutral-gray-120)]">
                                    {normalizeValue(injectionInfo?.administration_notes) || '—'}
                                  </td>
                                </tr>);
                            })}
                          </tbody>
                        </table>) : (<table className="w-full min-w-[760px] text-sm">
                          <thead>
                            <tr className="border-b border-[var(--neutral-gray-30)] text-left text-xs uppercase tracking-wide text-[var(--neutral-gray-90)]">
                              <th className="py-2 pr-3 font-semibold">Date</th>
                              {!false ? <th className="py-2 pr-3 font-semibold">Time</th> : null}
                              <th className="py-2 pr-3 font-semibold">{'Dose'}</th>
                              <th className="py-2 pr-3 font-semibold">Status</th>
                              <th className="py-2 pr-0 font-semibold">{isImOtherMedication ? 'Site' : 'Witnessed By Other'}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {timelinePage.rows.map((timeline) => (<tr className="border-b border-[var(--neutral-gray-20)] align-top last:border-b-0" key={timeline.id}>
                                <td className="py-2 pr-3 text-[var(--neutral-gray-120)]">{formatDate(timeline.date)}</td>
                                {!false ? (<td className="py-2 pr-3 text-[var(--neutral-gray-120)]">{formatTimeLabel(timeline.time)}</td>) : null}
                                <td className="py-2 pr-3 text-[var(--neutral-gray-120)]">
                                  {formatMedicationTimelineDoseLabel(medication, timeline)}
                                </td>
                                <td className="py-2 pr-3">
                                  {canEditMedicationTimelineStatus ? (<Select disabled={false} onValueChange={(value) => handleSaveMedicationTimelineStatus(timeline, value as 'notTaken' | 'taken' | 'unset')} value={normalizeValue(timeline.status) || 'unset'}>
                                      <SelectTrigger className="h-8 w-[140px]">
                                        <SelectValue placeholder="Status"/>
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="unset">Unset</SelectItem>
                                        <SelectItem value="taken">Taken</SelectItem>
                                        <SelectItem value="notTaken">Not Taken</SelectItem>
                                      </SelectContent>
                                    </Select>) : (<div className="text-[var(--neutral-gray-120)]">
                                      {formatTimelineStatusLabel(timeline.status)}
                                    </div>)}
                                </td>
                                <td className="py-2 pr-0">
                                  {isImOtherMedication ? (canEditMedicationTimelineSite ? (<Select disabled={false} onValueChange={(value) => handleSaveMedicationTimelineSite(timeline, value === SELECT_EMPTY_VALUE ? '' : value)} value={normalizeValue(timeline.site) || SELECT_EMPTY_VALUE}>
                                      <SelectTrigger className="h-8 w-[170px]">
                                        <SelectValue placeholder="Site"/>
                                      </SelectTrigger>
                                      <SelectContent>
                                          {MEDICATION_TIMELINE_SITE_OPTIONS.map((option) => (<SelectItem key={option.value || SELECT_EMPTY_VALUE} value={option.value || SELECT_EMPTY_VALUE}>
                                              {option.label}
                                            </SelectItem>))}
                                        </SelectContent>
                                      </Select>) : (<div className="text-[var(--neutral-gray-120)]">{normalizeValue(timeline.site) || 'Not Set'}</div>)) : canEditMedicationTimelineWitness ? (<Select disabled={normalizeValue(timeline.status) === 'notTaken'
                                        || normalizeValue(timeline.status) === 'unset'
                                        || (false)} onValueChange={(value) => handleSaveMedicationTimelineWitness(timeline, value === 'true')} value={timeline.witnessed_by_other ? 'true' : 'false'}>
                                      <SelectTrigger className="h-8 w-[150px]">
                                        <SelectValue placeholder="Witnessed"/>
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="false">No</SelectItem>
                                        <SelectItem value="true">Yes</SelectItem>
                                      </SelectContent>
                                    </Select>) : (<div className="text-[var(--neutral-gray-120)]">
                                      {timeline.witnessed_by_other ? 'Yes' : 'No'}
                                    </div>)}
                                </td>
                              </tr>))}
                          </tbody>
                        </table>)}
                      {null}
                    </div>) : null}
                </div>);
            })}
            </MedicationColumn>))}
          </CardContent>
        </Card>

        {!false ? (<Card>
            <CardHeader>
              <CardTitle>Activation History</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pt-4">
              {activationHistory.length < 1 ? (<div className="text-sm text-[var(--neutral-gray-100)]">No status history recorded.</div>) : null}

              {activationHistory.map((item, index) => (<div key={`${item.date || 'history'}-${index}`} className="border-l-2 border-[var(--neutral-gray-30)] pl-3">
                  <div className="text-sm font-medium capitalize text-[var(--neutral-gray-120)]">
                    {normalizeValue(item.status) || 'Status update'}
                  </div>
                  <div className="text-sm text-[var(--neutral-gray-100)]">
                    {normalizeValue(item.fullname) || 'Unknown user'}
                    {normalizeValue(item.username) ? ` (${normalizeValue(item.username)})` : ''}
                  </div>
                  <div className="text-sm text-[var(--neutral-gray-100)]">{formatDateTime(item.date)}</div>
                  {normalizeValue(item.deactivation_reason) ? (<div className="mt-1 text-sm text-[var(--neutral-gray-120)]">
                      Reason: {normalizeValue(item.deactivation_reason)}
                    </div>) : null}
                </div>))}
            </CardContent>
          </Card>) : null}
      </div>

      {!false ? (<Card>
          <CardHeader>
            <CardTitle>Client Notes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 pt-4">
            {notes.length < 1 ? (<div className="text-sm text-[var(--neutral-gray-100)]">No notes recorded.</div>) : null}

            {notes.map((note) => (<div key={note.id} className="rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-30)] p-3">
                <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                  <div className="text-sm font-medium text-[var(--neutral-gray-120)]">
                    {normalizeValue(note.entryby) || 'MedCall'}
                  </div>
                  <div className="text-sm text-[var(--neutral-gray-100)]">{formatDateTime(note.entrydate)}</div>
                </div>
                <div className="whitespace-pre-wrap text-sm text-[var(--neutral-gray-120)]">
                  {normalizeValue(note.notes) || '—'}
                </div>
              </div>))}
          </CardContent>
        </Card>) : null}

      <Dialog open={activeDialog === 'edit'} onOpenChange={(open: boolean) => {
            if (!open) {
                closeDialog();
            }
        }}>
        <DialogContent className={'max-w-2xl p-0'} contentHeight="auto" contentMaxHeight="90vh" contentMaxWidth={'900px'} contentTop={undefined} contentWidth={'760px'} hideCloseButton={false} onEscapeKeyDown={undefined} onOpenAutoFocus={undefined} overlayClassName={undefined}>
          <form className="flex max-h-[90vh] flex-col overflow-hidden" onSubmit={handleSaveProfile}>
            <DialogHeader className="border-b border-[var(--neutral-gray-40)] px-5 py-4">
              <DialogTitle>Edit Client</DialogTitle>
              {identityDialogCopy.profileDescription ? (<DialogDescription>{identityDialogCopy.profileDescription}</DialogDescription>) : null}
            </DialogHeader>
            <div className={`grid gap-4 overflow-y-auto px-5 py-4 ${'md:grid-cols-2'}`}>
              {hasFullAccess ? (<DialogField label="Program" required>
                  <Select onValueChange={(value) => {
                setProfileDraft((current) => {
                    const nextProgram = value === SELECT_EMPTY_VALUE ? '' : value;
                    if (!participantProgramUsesFacility(nextProgram)) {
                        return {
                            ...current,
                            optionFacilityId: '',
                            program: nextProgram,
                        };
                    }
                    return {
                        ...current,
                        program: nextProgram,
                    };
                });
            }} value={profileDraft.program || undefined}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select program"/>
                    </SelectTrigger>
                    <SelectContent>
                      {programOptions.map((option) => (<SelectItem key={`program-${normalizeValue(option.value)}`} value={normalizeValue(option.value)}>
                          {normalizeValue(option.label) || normalizeValue(option.value)}
                        </SelectItem>))}
                    </SelectContent>
                  </Select>
                </DialogField>) : null}

              {profileProgramUsesFacility ? (<DialogField label="Facility" required>
                  <Select onValueChange={(value) => {
                setProfileDraft((current) => ({
                    ...current,
                    optionFacilityId: value === SELECT_EMPTY_VALUE ? '' : value,
                }));
            }} value={profileDraft.optionFacilityId || undefined}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select facility"/>
                    </SelectTrigger>
                    <SelectContent>
                      {facilityOptions.map((option) => (<SelectItem key={`facility-${normalizeValue(option.value)}`} value={normalizeValue(option.value)}>
                          {normalizeValue(option.label) || normalizeValue(option.value)}
                        </SelectItem>))}
                    </SelectContent>
                  </Select>
                </DialogField>) : null}

              <DialogField label="Provider">
                <Select onValueChange={(value) => {
            setProfileDraft((current) => ({
                ...current,
                provider: value === SELECT_EMPTY_VALUE ? '' : value,
            }));
        }} value={profileDraft.provider || SELECT_EMPTY_VALUE}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select provider"/>
                  </SelectTrigger>
                  <SelectContent>
                    {!false ? (<SelectItem value={SELECT_EMPTY_VALUE}>Unassigned</SelectItem>) : null}
                    {providerOptions.map((option) => (<SelectItem key={`provider-${option.value}`} value={option.value}>
                        {option.label}
                      </SelectItem>))}
                  </SelectContent>
                </Select>
              </DialogField>

              {hasFullAccess ? (<DialogField label="CID" required>
                  <Input inputMode="numeric" onChange={(event) => {
                setProfileDraft((current) => ({
                    ...current,
                    cid: normalizeMedcallProfileCidDraft(routeBase, event.target.value),
                }));
            }} value={profileDraft.cid}/>
                </DialogField>) : null}

              {hasFullAccess ? (<DialogField label={identityDialogCopy.dobLabel} required>
                  <Input onChange={(event) => {
                setProfileDraft((current) => ({
                    ...current,
                    dob: event.target.value,
                }));
            }} type="date" value={profileDraft.dob}/>
                </DialogField>) : null}
            </div>
            <DialogFooter className="border-t border-[var(--neutral-gray-40)] px-5 py-4">
              <Button disabled={pendingAction !== null} onClick={closeDialog} type="button" variant="outline">
                Cancel
              </Button>
              <Button disabled={pendingAction !== null} type="submit">
                {identityDialogCopy.profileSubmitLabel}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={activeDialog === 'staff'} onOpenChange={(open: boolean) => {
            if (!open) {
                closeDialog();
            }
        }}>
        <DialogContent className={'max-w-2xl p-0'} contentHeight="auto" contentMaxHeight="90vh" contentMaxWidth={'900px'} contentTop={undefined} contentWidth={'720px'} hideCloseButton={false} overlayClassName={undefined}>
          {(<div className="flex max-h-[90vh] flex-col overflow-hidden">
            <DialogHeader className="border-b border-[var(--neutral-gray-40)] px-5 py-4">
              <DialogTitle>Staff Assignment</DialogTitle>
              <DialogDescription>Update the assigned staff member for this client.</DialogDescription>
            </DialogHeader>
            <div className="overflow-y-auto px-5 py-4">
              {hasFullAccess && staffHistory.length > 0 ? (<Tabs onValueChange={setStaffDialogTab} value={staffDialogTab}>
                  <TabsList className="mb-4 inline-flex h-auto rounded-[var(--border-radius-medium)] bg-[var(--surface-muted)] p-1">
                    <TabsTrigger className="rounded-[var(--border-radius-small)] px-3 py-1.5" value="form">
                      Form
                    </TabsTrigger>
                    <TabsTrigger className="rounded-[var(--border-radius-small)] px-3 py-1.5" value="history">
                      History
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent className="mt-0" value="form">
                    <form className="space-y-4" onSubmit={handleSaveStaffAssignment}>
                      <DialogField label="Staff" required>
                        <Select onValueChange={setStaffDraft} value={staffDraft || undefined}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select staff"/>
                          </SelectTrigger>
                          <SelectContent>
                            {availableStaffOptions.map((option) => (<SelectItem key={`staff-${option.id}`} value={`${option.id}`}>
                                {getMedcallStaffOptionLabel(option)}
                              </SelectItem>))}
                          </SelectContent>
                        </Select>
                      </DialogField>
                      <DialogFooter className="px-0 pt-2">
                        <Button disabled={pendingAction !== null} onClick={closeDialog} type="button" variant="outline">
                          Cancel
                        </Button>
                        <Button disabled={pendingAction !== null} type="submit">
                          {pendingAction === 'staff' ? 'Saving...' : 'Save Assignment'}
                        </Button>
                      </DialogFooter>
                    </form>
                  </TabsContent>
                  <TabsContent className="mt-0 space-y-3" value="history">
                    {staffHistory.map((item, index) => (<div key={`${item.date || 'staff-history'}-${index}`} className="rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-30)] px-3 py-2">
                        <div className="text-sm font-medium text-[var(--neutral-gray-120)]">
                          {normalizeValue(item.fullname) || 'Unknown user'}
                          {normalizeValue(item.username) ? ` (${normalizeValue(item.username)})` : ''}
                        </div>
                        <div className="text-sm text-[var(--neutral-gray-100)]">{formatDateTime(item.date)}</div>
                      </div>))}
                  </TabsContent>
                </Tabs>) : (<form className="space-y-4" onSubmit={handleSaveStaffAssignment}>
                  <DialogField label="Staff" required>
                    <Select onValueChange={setStaffDraft} value={staffDraft || undefined}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select staff"/>
                      </SelectTrigger>
                      <SelectContent>
                        {availableStaffOptions.map((option) => (<SelectItem key={`staff-${option.id}`} value={`${option.id}`}>
                            {getMedcallStaffOptionLabel(option)}
                          </SelectItem>))}
                      </SelectContent>
                    </Select>
                  </DialogField>
                  <DialogFooter className="px-0 pt-2">
                    <Button disabled={pendingAction !== null} onClick={closeDialog} type="button" variant="outline">
                      Cancel
                    </Button>
                    <Button disabled={pendingAction !== null} type="submit">
                      {pendingAction === 'staff' ? 'Saving...' : 'Save Assignment'}
                    </Button>
                  </DialogFooter>
                </form>)}
            </div>
          </div>)}
        </DialogContent>
      </Dialog>

      <Dialog open={activeDialog === 'nurse'} onOpenChange={(open: boolean) => {
            if (!open) {
                closeDialog();
            }
        }}>
        <DialogContent className={'max-w-2xl p-0'} contentHeight="auto" contentMaxHeight="90vh" contentMaxWidth={'900px'} contentTop={undefined} contentWidth={'720px'} hideCloseButton={false} overlayClassName={undefined}>
          {(<form className="flex max-h-[90vh] flex-col overflow-hidden" onSubmit={handleSaveNurse}>
            <DialogHeader className="border-b border-[var(--neutral-gray-40)] px-5 py-4">
              <DialogTitle>Nurse Assignment</DialogTitle>
              <DialogDescription>Update the nurse contact for this client.</DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 overflow-y-auto px-5 py-4 md:grid-cols-2">
              <DialogField label="First Name" required>
                <Input onChange={(event) => {
                setNurseDraft((current) => ({
                    ...current,
                    firstname: event.target.value,
                }));
            }} value={nurseDraft.firstname}/>
              </DialogField>
              <DialogField label="Last Name" required>
                <Input onChange={(event) => {
                setNurseDraft((current) => ({
                    ...current,
                    lastname: event.target.value,
                }));
            }} value={nurseDraft.lastname}/>
              </DialogField>
              <DialogField label="Title" required>
                <Select onValueChange={(value) => {
                setNurseDraft((current) => ({
                    ...current,
                    title: value,
                }));
            }} value={nurseDraft.title || undefined}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select title"/>
                  </SelectTrigger>
                  <SelectContent>
                    {NURSE_TITLES.map((title) => (<SelectItem key={`nurse-title-${title}`} value={title}>
                        {title}
                      </SelectItem>))}
                  </SelectContent>
                </Select>
              </DialogField>
              <DialogField label="Email" required>
                <Input onChange={(event) => {
                setNurseDraft((current) => ({
                    ...current,
                    email: event.target.value,
                }));
            }} type="email" value={nurseDraft.email}/>
              </DialogField>
              <DialogField label="Retype Email" required>
                <Input onChange={(event) => {
                setNurseDraft((current) => ({
                    ...current,
                    confirmEmail: event.target.value,
                }));
            }} type="email" value={nurseDraft.confirmEmail}/>
              </DialogField>
            </div>
            <DialogFooter className="border-t border-[var(--neutral-gray-40)] px-5 py-4">
              <Button disabled={pendingAction !== null} onClick={closeDialog} type="button" variant="outline">
                Cancel
              </Button>
              <Button disabled={pendingAction !== null} type="submit">
                {pendingAction === 'nurse' ? 'Saving...' : 'Save Nurse'}
              </Button>
            </DialogFooter>
          </form>)}
        </DialogContent>
      </Dialog>

      <Dialog open={activeDialog === 'psychiatrist'} onOpenChange={(open: boolean) => {
            if (!open) {
                closeDialog();
            }
        }}>
        <DialogContent className={'max-w-2xl p-0'} contentHeight="auto" contentMaxHeight="90vh" contentMaxWidth={'900px'} contentTop={undefined} contentWidth={'720px'} hideCloseButton={false} overlayClassName={undefined}>
          {(<form className="flex max-h-[90vh] flex-col overflow-hidden" onSubmit={handleSavePsychiatrist}>
            <DialogHeader className="border-b border-[var(--neutral-gray-40)] px-5 py-4">
              <DialogTitle>Psychiatrist Assignment</DialogTitle>
              <DialogDescription>Update the psychiatrist contact for this client.</DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 overflow-y-auto px-5 py-4 md:grid-cols-2">
              <DialogField label="First Name" required>
                <Input onChange={(event) => {
                setPsychiatristDraft((current) => ({
                    ...current,
                    firstname: event.target.value,
                }));
            }} value={psychiatristDraft.firstname}/>
              </DialogField>
              <DialogField label="Last Name" required>
                <Input onChange={(event) => {
                setPsychiatristDraft((current) => ({
                    ...current,
                    lastname: event.target.value,
                }));
            }} value={psychiatristDraft.lastname}/>
              </DialogField>
              <DialogField label="Title" required>
                <Select onValueChange={(value) => {
                setPsychiatristDraft((current) => ({
                    ...current,
                    title: value,
                }));
            }} value={psychiatristDraft.title || undefined}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select title"/>
                  </SelectTrigger>
                  <SelectContent>
                    {PSYCHIATRIST_TITLES.map((title) => (<SelectItem key={`psychiatrist-title-${title}`} value={title}>
                        {title}
                      </SelectItem>))}
                  </SelectContent>
                </Select>
              </DialogField>
              <DialogField label="Email">
                <Input onChange={(event) => {
                setPsychiatristDraft((current) => ({
                    ...current,
                    email: event.target.value,
                }));
            }} type="email" value={psychiatristDraft.email}/>
              </DialogField>
              <DialogField label="Retype Email">
                <Input onChange={(event) => {
                setPsychiatristDraft((current) => ({
                    ...current,
                    confirmEmail: event.target.value,
                }));
            }} type="email" value={psychiatristDraft.confirmEmail}/>
              </DialogField>
            </div>
            <DialogFooter className="border-t border-[var(--neutral-gray-40)] px-5 py-4">
              <Button disabled={pendingAction !== null} onClick={closeDialog} type="button" variant="outline">
                Cancel
              </Button>
              <Button disabled={pendingAction !== null} type="submit">
                {pendingAction === 'psychiatrist' ? 'Saving...' : 'Save Psychiatrist'}
              </Button>
            </DialogFooter>
          </form>)}
        </DialogContent>
      </Dialog>

      <Dialog open={activeDialog === 'aliases'} onOpenChange={(open: boolean) => {
            if (!open) {
                closeDialog();
            }
        }}>
        <DialogContent aria-describedby={undefined} className={'max-w-2xl p-0'} contentHeight="auto" contentMaxHeight="90vh" contentMaxWidth={'760px'} contentTop={undefined} contentWidth={'620px'} hideCloseButton={false} onEscapeKeyDown={undefined} onOpenAutoFocus={undefined} overlayClassName={undefined}>
          <form className="flex max-h-[90vh] flex-col overflow-hidden" onSubmit={handleAddAlias}>
            <DialogHeader className="border-b border-[var(--neutral-gray-40)] px-5 py-4">
              <DialogTitle>Client Aliases</DialogTitle>
            </DialogHeader>
            <div className="space-y-5 overflow-y-auto px-5 py-4">
              {(<div className="space-y-2">
                <div className="text-sm font-semibold text-[var(--neutral-gray-120)]">Aliases</div>
                {participant.aliases && participant.aliases.length > 0 ? (<ol className="space-y-2 border-l border-[var(--neutral-gray-40)] pl-5">
                    {participant.aliases.map((alias, index) => (<li className="relative text-sm text-[var(--neutral-gray-120)]" key={`participant-alias-${index + 1}`}>
                        <span className="absolute -left-[24px] top-1.5 h-2 w-2 rounded-full bg-[var(--neutral-gray-60)]"/>
                        {[alias.firstname, alias.lastname].filter(Boolean).join(' ').trim()}
                      </li>))}
                  </ol>) : (<div className="text-sm text-[var(--neutral-gray-100)]">None</div>)}
              </div>)}
              <div className="text-sm font-semibold text-[var(--neutral-gray-120)]">Add an alias:</div>
              {showAliasEntryFields ? (<div className={`grid gap-4 ${'md:grid-cols-2'}`}>
                  <DialogField label="Firstname" required>
                    <Input onChange={(event) => setAliasDraft((current) => ({ ...current, firstname: event.target.value }))} value={aliasDraft.firstname}/>
                  </DialogField>
                  <DialogField label="Lastname" required>
                    <Input onChange={(event) => setAliasDraft((current) => ({ ...current, lastname: event.target.value }))} value={aliasDraft.lastname}/>
                  </DialogField>
                </div>) : null}
            </div>
            <DialogFooter className="border-t border-[var(--neutral-gray-40)] px-5 py-4">
              <Button disabled={pendingAction !== null} onClick={closeDialog} type="button" variant="outline">
                Cancel
              </Button>
              <Button disabled={pendingAction !== null} type="submit">
                {pendingAction === 'alias' && !false ? 'Saving...' : 'Submit'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {null}

      <Dialog open={!false && activeDialog === 'notes'} onOpenChange={(open: boolean) => {
            if (!open) {
                closeDialog();
            }
        }}>
        <DialogContent className={'max-w-4xl p-0'} contentHeight="auto" contentMaxHeight="90vh" contentMaxWidth="960px" contentWidth="820px">
          <div className="flex max-h-[90vh] flex-col overflow-hidden">
            <DialogHeader className="border-b border-[var(--neutral-gray-40)] px-5 py-4">
              <DialogTitle>Client Notes</DialogTitle>
              <DialogDescription>Review the note history and add a new note.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 overflow-y-auto px-5 py-4">
              <form className="space-y-3" onSubmit={handleAddNote}>
                <DialogField label="New Note">
                  <Textarea onChange={(event) => setNoteDraft(event.target.value)} placeholder="Enter a note" rows={3} value={noteDraft}/>
                </DialogField>
                <div className="flex justify-end">
                  <Button disabled={pendingAction !== null || !normalizeValue(noteDraft)} type="submit">
                    {pendingAction === 'note' ? 'Saving...' : 'Add Note'}
                  </Button>
                </div>
              </form>
              <div className="space-y-3">
                {notes.length < 1 ? (<div className="text-sm text-[var(--neutral-gray-100)]">No notes recorded for this client.</div>) : null}
                {notes.map((note) => (<div key={`dialog-note-${note.id}`} className="rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-30)] px-3 py-3">
                    <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                      <div className="text-sm font-medium text-[var(--neutral-gray-120)]">
                        {normalizeValue(note.entryby) || 'MedCall'}
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-sm text-[var(--neutral-gray-100)]">{formatDateTime(note.entrydate)}</div>
                        <Button aria-label="Delete this note" className="h-7 w-7 p-0 text-[var(--semantic-error)]" disabled={pendingAction !== null} onClick={() => setDeleteNoteTarget(note)} size="sm" type="button" variant="ghost">
                          <Trash2 className="h-4 w-4"/>
                        </Button>
                      </div>
                    </div>
                    <div className="whitespace-pre-wrap text-sm text-[var(--neutral-gray-120)]">
                      {normalizeValue(note.notes) || '—'}
                    </div>
                  </div>))}
              </div>
            </div>
            <DialogFooter className="border-t border-[var(--neutral-gray-40)] px-5 py-4">
              <Button onClick={closeDialog} type="button" variant="outline">
                Close
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <YesNoDialog cancelLabel="Cancel" confirmClassName="border-[var(--semantic-error)] bg-[var(--semantic-error)] hover:opacity-90" confirmDisabled={pendingAction !== null} confirmLabel="Delete" description="Delete this note?" onConfirm={handleDeleteNote} onOpenChange={(open) => {
            if (open || pendingAction !== null) {
                return;
            }
            setDeleteNoteTarget(null);
        }} open={Boolean(deleteNoteTarget)} title="Delete this note?"/>

      <MedcallSendMessageDialog onOpenChange={(open) => {
            if (open) {
                setActiveDialog('message');
                return;
            }
            closeDialog();
        }} open={activeDialog === 'message'} participant={participant} participantId={participant.id} preserveLegacySemantics={false}/>

      <MedcallAdherenceReportDialog legacyPresentation={false} onOpenChange={(open) => {
            if (!open) {
                closeDialog();
            }
        }} open={activeDialog === 'report'} participant={participant}/>

      <MedcallMedicationListDialog legacyPresentation={false} onOpenChange={(open) => {
            if (!open) {
                closeDialog();
            }
        }} open={activeDialog === 'med-list'} participant={participant}/>

      <Dialog open={activeDialog === 'medication'} onOpenChange={(open: boolean) => {
            if (!open) {
                closeDialog();
            }
        }}>
        <DialogContent className={'max-w-4xl p-0'} contentHeight="auto" contentMaxHeight="90vh" contentMaxWidth={'980px'} contentWidth={'860px'} hideCloseButton={false} overlayClassName={undefined}>
          {(<form className={'flex max-h-[90vh] flex-col overflow-hidden'} onSubmit={handleSaveMedication}>
            <DialogHeader className={'border-b border-[var(--neutral-gray-40)] px-5 py-4'}>
              <DialogTitle>{editingMedicationId ? 'Edit Medication' : 'Add Medication'}</DialogTitle>
              {!false ? <DialogDescription>Update the medication schedule and related details for this client.</DialogDescription> : null}
            </DialogHeader>
            <div className={'grid gap-4 overflow-y-auto px-5 py-4 md:grid-cols-2'}>
              <DialogField label={showLegacyMedicationSearchOnly ? 'Medicine Search' : 'Medication Name'} required={!false}>
                <div className="relative">
                  <Input autoComplete="off" disabled={false} onChange={(event) => {
                setMedicationDraft((current) => ({
                    ...current,
                    medicationExternalId: '',
                    medicineDetail: '',
                    medicineName: event.target.value,
                    strength: '',
                }));
                setMedicationStrengthOptions([]);
                setUsesCustomMedicationStrength(false);
            }} placeholder="Please enter 4 or more charachters" role={undefined} type={undefined} value={medicationDraft.medicineName}/>
                  {medicationSearchLoading ? (<div className="mt-1 text-xs text-[var(--neutral-gray-90)]">Searching medications...</div>) : null}
                  {medicationSearchResults.length > 0 ? (<div className="absolute z-50 mt-1 max-h-64 w-full overflow-y-auto rounded border border-[var(--neutral-gray-40)] bg-white shadow-lg">
                      {medicationSearchResults.map((item, index) => {
                    const itemName = normalizeValue(item.name || item.displayName);
                    const itemKey = normalizeValue(item.externalId || item.uuid || item.drugbankPcid) || `${itemName}-${index}`;
                    return (<button className="block w-full border-b border-[var(--neutral-gray-20)] px-3 py-2 text-left text-sm text-[var(--neutral-gray-120)] hover:bg-[var(--surface-muted)] last:border-b-0" key={itemKey} onClick={() => {
                            void handleMedicationCatalogSelection(item);
                        }} type="button">
                            <span>{itemName}</span>
                            {item.isCustom ? (<span className="ml-2 text-xs text-[var(--neutral-gray-90)]">Custom</span>) : null}
                          </button>);
                })}
                    </div>) : null}
                </div>
              </DialogField>

              <DialogField label="Dose" required>
                {((false || medicationStrengthOptions.length > 0) && !usesCustomMedicationStrength) ? (<Select onValueChange={(value) => {
                    if (value === '__custom__') {
                        setUsesCustomMedicationStrength(true);
                        setMedicationDraft((current) => ({ ...current, strength: '' }));
                        return;
                    }
                    setMedicationDraft((current) => ({
                        ...current,
                        strength: resolveMedcallStoredStrength(medicationStrengthOptions, value),
                    }));
                }} value={resolveMedcallStrengthControlValue(medicationStrengthOptions, medicationDraft.strength) || undefined}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select dose"/>
                    </SelectTrigger>
                    <SelectContent>
                      {medicationDraft.strength && !medicationStrengthOptions.some((option) => (getMedcallStrengthOptionLabel(option) === medicationDraft.strength
                    || getMedcallStrengthOptionValue(option) === medicationDraft.strength)) ? (<SelectItem value={medicationDraft.strength}>{medicationDraft.strength}</SelectItem>) : null}
                      {medicationStrengthOptions.map((option, index) => {
                    const strengthOptionValue = getMedcallStrengthOptionValue(option);
                    const strengthOptionLabel = getMedcallStrengthOptionLabel(option);
                    return (<SelectItem key={`medication-strength-${strengthOptionValue}-${index}`} value={strengthOptionValue}>
                            {strengthOptionLabel}
                          </SelectItem>);
                })}
                      <SelectItem value="__custom__">Custom</SelectItem>
                    </SelectContent>
                  </Select>) : (<div className="space-y-1">
                    <Input onChange={(event) => {
                    setMedicationDraft((current) => ({
                        ...current,
                        strength: event.target.value,
                    }));
                }} placeholder="Enter dose" value={medicationDraft.strength}/>
                    {usesCustomMedicationStrength && medicationStrengthOptions.length > 0 ? (<button className="text-xs text-[var(--primary)] underline" onClick={() => {
                        setUsesCustomMedicationStrength(false);
                        setMedicationDraft((current) => ({ ...current, strength: '' }));
                    }} type="button">
                        Choose a listed dose
                      </button>) : null}
                  </div>)}
              </DialogField>

              <DialogField label="Route" required>
                <Select onValueChange={handleMedicationRouteChange} value={medicationDraft.route || undefined}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select route"/>
                  </SelectTrigger>
                  <SelectContent>
                    {MEDICATION_ROUTE_OPTIONS.map((option) => (<SelectItem key={`med-route-${option}`} value={option}>
                        {option}
                      </SelectItem>))}
                  </SelectContent>
                </Select>
              </DialogField>

              {medicationDraft.route !== 'IM-LAI' ? (<DialogField label="PRN">
                  <Select onValueChange={(value) => {
                    setMedicationDraft((current) => ({
                        ...current,
                        prn: value === 'true',
                    }));
                }} value={medicationDraft.prn ? 'true' : 'false'}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select PRN option"/>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="false">No</SelectItem>
                      <SelectItem value="true">Yes</SelectItem>
                    </SelectContent>
                  </Select>
                </DialogField>) : null}

              {medicationDraft.route !== 'IM-LAI' ? (<DialogField label="Importance">
                  <Select onValueChange={(value) => {
                    setMedicationDraft((current) => ({
                        ...current,
                        importanceLevel: value,
                    }));
                }} value={medicationDraft.importanceLevel || '2'}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select importance"/>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">Low</SelectItem>
                      <SelectItem value="1">Medium</SelectItem>
                      <SelectItem value="2">High</SelectItem>
                    </SelectContent>
                  </Select>
                </DialogField>) : null}

              <DialogField label="Frequency" required>
                <Select onValueChange={handleMedicationFrequencyChange} value={medicationDraft.frequency || undefined}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select frequency"/>
                  </SelectTrigger>
                  <SelectContent>
                    {frequencyOptions.map((option) => (<SelectItem key={`med-frequency-${option.value}`} value={option.value}>
                        {option.label}
                      </SelectItem>))}
                  </SelectContent>
                </Select>
              </DialogField>

              <DialogField label="Schedule" required>
                <Select onValueChange={(value) => {
                setMedicationDraft((current) => ({
                    ...current,
                    timesPerDay: value,
                }));
            }} value={medicationDraft.timesPerDay || undefined}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select schedule"/>
                  </SelectTrigger>
                  <SelectContent>
                    {scheduleOptions.map((option) => (<SelectItem key={`med-schedule-${option}`} value={option}>
                        {formatMedicationScheduleLabel(option)}
                      </SelectItem>))}
                  </SelectContent>
                </Select>
              </DialogField>

              {medicationDraft.frequency === 'custom_days' ? (<div className="space-y-2 md:col-span-2">
                  <div className="text-sm font-medium text-[var(--neutral-gray-120)]">Custom Days</div>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
                    {MEDICATION_DAY_OPTIONS.map((option) => {
                    const checked = medicationDraft.customDays.includes(option.value);
                    return (<label key={`med-day-${option.value}`} className="flex items-center gap-2 rounded-[var(--border-radius-small)] border border-[var(--neutral-gray-30)] px-3 py-2 text-sm text-[var(--neutral-gray-120)]">
                          <Checkbox checked={checked} onCheckedChange={(value) => handleMedicationDayToggle(option.value, value === true)}/>
                          <span>{option.label}</span>
                        </label>);
                })}
                  </div>
                </div>) : null}

              {medicationDraft.route !== 'IM-LAI' ? (<DialogField label="Start Date and Time" required>
                  <Input onChange={(event) => {
                    setMedicationDraft((current) => ({
                        ...current,
                        startDate: event.target.value,
                    }));
                }} type="datetime-local" value={medicationDraft.startDate}/>
                </DialogField>) : null}

              {medicationDraft.route !== 'IM-LAI' ? (<div className="space-y-2">
                  <div className="text-sm font-medium text-[var(--neutral-gray-120)]">Discontinue Date</div>
                  <div className="flex items-center justify-between rounded-[var(--border-radius-small)] border border-[var(--neutral-gray-30)] px-3 py-2">
                    <span className="text-sm text-[var(--neutral-gray-120)]">Enable discontinue date</span>
                    <Switch checked={medicationDraft.stopDateEnabled} onCheckedChange={(checked) => {
                    setMedicationDraft((current) => ({
                        ...current,
                        stopDate: checked ? current.stopDate : '',
                        stopDateEnabled: checked,
                    }));
                }}/>
                  </div>
                </div>) : null}

              {medicationDraft.route !== 'IM-LAI' && medicationDraft.stopDateEnabled ? (<DialogField label="Discontinue Date and Time" required>
                  <Input onChange={(event) => {
                    setMedicationDraft((current) => ({
                        ...current,
                        stopDate: event.target.value,
                    }));
                }} type="datetime-local" value={medicationDraft.stopDate}/>
                </DialogField>) : null}

              {medicationDraft.route !== 'IM-LAI' && !isMedicationReadOnlyProgram ? (<div className="space-y-2">
                  <div className="text-sm font-medium text-[var(--neutral-gray-120)]">Runs Out Date</div>
                  <div className="flex items-center justify-between rounded-[var(--border-radius-small)] border border-[var(--neutral-gray-30)] px-3 py-2">
                    <span className="text-sm text-[var(--neutral-gray-120)]">Track runs out date</span>
                    <Switch checked={medicationDraft.refillStatus} onCheckedChange={(checked) => {
                    setMedicationDraft((current) => ({
                        ...current,
                        refillDate: checked ? current.refillDate : '',
                        refillStatus: checked,
                    }));
                }}/>
                  </div>
                </div>) : null}

              {medicationDraft.route !== 'IM-LAI' && !isMedicationReadOnlyProgram && medicationDraft.refillStatus ? (<DialogField label="Runs Out Date" required>
                  <Input onChange={(event) => {
                    setMedicationDraft((current) => ({
                        ...current,
                        refillDate: event.target.value,
                    }));
                }} type="date" value={medicationDraft.refillDate}/>
                </DialogField>) : null}

              {medicationDraft.route === 'IM-LAI' ? (<DialogField label="Date Due" required>
                  <Input onChange={(event) => {
                    setMedicationDraft((current) => ({
                        ...current,
                        dateDue: event.target.value,
                    }));
                }} type="date" value={medicationDraft.dateDue}/>
                </DialogField>) : null}

              {medicationDraft.route === 'IM-LAI' ? (<DialogField label="Injection Status">
                  <Select onValueChange={(value) => {
                    setMedicationDraft((current) => ({
                        ...current,
                        dateInjectionArrived: value === '2' ? current.dateInjectionArrived : '',
                        dateGiven: value === '2' ? current.dateGiven : '',
                        onSite: value,
                    }));
                }} value={medicationDraft.onSite || '0'}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select injection status"/>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">Not Yet On-Site</SelectItem>
                      <SelectItem value="1">N.A.</SelectItem>
                      <SelectItem value="2">On-Site</SelectItem>
                    </SelectContent>
                  </Select>
                </DialogField>) : null}

              {medicationDraft.route === 'IM-LAI' && medicationDraft.onSite === '2' ? (<DialogField label="Date Injection Arrived On-Site" required>
                  <Input onChange={(event) => {
                    setMedicationDraft((current) => ({
                        ...current,
                        dateInjectionArrived: event.target.value,
                    }));
                }} type="date" value={medicationDraft.dateInjectionArrived}/>
                </DialogField>) : null}

              {medicationDraft.route === 'IM-LAI' ? (<DialogField label="Date Given">
                  <Input disabled={medicationDraft.onSite !== '2'} onChange={(event) => {
                    setMedicationDraft((current) => ({
                        ...current,
                        administrationNotes: event.target.value ? current.administrationNotes : '',
                        dateGiven: event.target.value,
                    }));
                }} type="date" value={medicationDraft.dateGiven}/>
                </DialogField>) : null}

              {medicationDraft.route === 'IM-LAI' ? (<div className="md:col-span-2">
                  <DialogField label="Injection Site Location">
                    <Input onChange={(event) => {
                    setMedicationDraft((current) => ({
                        ...current,
                        administrationNotes: event.target.value,
                    }));
                }} value={medicationDraft.administrationNotes}/>
                  </DialogField>
                </div>) : null}
            </div>
            <DialogFooter className={'border-t border-[var(--neutral-gray-40)] px-5 py-4'}>
              {null}
              <Button disabled={pendingAction !== null} id="medcall-medication-save-button" type="submit">
                {!false && editingMedicationId ? <Save className="h-4 w-4"/> : null}
                {!false && !editingMedicationId ? <Plus className="h-4 w-4"/> : null}
                {pendingAction === 'medication-create' || pendingAction === 'medication-update'
                ? 'Saving...'
                : editingMedicationId
                    ? 'Save Medication'
                    : 'Add Medication'}
              </Button>
              {!false ? <Button disabled={pendingAction !== null} id="medcall-medication-cancel-button" onClick={closeDialog} type="button" variant="outline">
                Cancel
              </Button> : null}
            </DialogFooter>
            </form>)}
        </DialogContent>
      </Dialog>

      <Dialog open={projectSitesTrackerSelection !== null} onOpenChange={(open: boolean) => {
            if (!open) {
                setProjectSitesTrackerSelection(null);
            }
        }}>
        <DialogContent className={'max-w-xl p-0'} contentHeight="auto" contentMaxHeight="90vh" contentMaxWidth="680px" contentWidth="560px">
          <form className="flex max-h-[90vh] flex-col overflow-hidden" onSubmit={handleSaveProjectSitesTrackerSelection}>
            <DialogHeader className="border-b border-[var(--neutral-gray-40)] px-5 py-4">
              <DialogTitle>Update Tracker</DialogTitle>
              <DialogDescription>
                {projectSitesTrackerSelection?.label || 'Select the tracker status for this date range.'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 overflow-y-auto px-5 py-4">
              <DialogField label="Status" required>
                <Select onValueChange={(value) => setProjectSitesTrackerStatus(value as 'notTaken' | 'taken')} value={projectSitesTrackerStatus}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status"/>
                  </SelectTrigger>
                  <SelectContent>
                    {PROJECT_SITES_TRACKER_STATUS_OPTIONS.map((option) => (<SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>))}
                  </SelectContent>
                </Select>
              </DialogField>
              <DialogField label="Notes">
                <Textarea onChange={(event) => setProjectSitesTrackerNotes(event.target.value)} rows={4} value={projectSitesTrackerNotes}/>
              </DialogField>
            </div>
            <DialogFooter className="border-t border-[var(--neutral-gray-40)] px-5 py-4">
              <Button disabled={pendingAction !== null} onClick={() => setProjectSitesTrackerSelection(null)} type="button" variant="outline">
                Cancel
              </Button>
              <Button disabled={pendingAction !== null} type="submit">
                {pendingAction === 'project-sites-tracker' ? 'Saving...' : 'Save Tracker'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {null}

      <Dialog open={!false && activeDialog === 'status'} onOpenChange={(open: boolean) => {
            if (!open) {
                closeDialog();
            }
        }}>
        <DialogContent className={'max-w-xl p-0'} contentHeight="auto" contentMaxHeight="90vh" contentMaxWidth="680px" contentWidth="560px">
          <form className="flex max-h-[90vh] flex-col overflow-hidden" onSubmit={handleSaveStatus}>
            <DialogHeader className="border-b border-[var(--neutral-gray-40)] px-5 py-4">
              <DialogTitle>{participant.active ? 'Deactivate Client' : 'Activate Client'}</DialogTitle>
              <DialogDescription>
                {participant.active
            ? 'Record the reason and date for deactivation.'
            : 'Confirm that this client should be marked active.'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 overflow-y-auto px-5 py-4">
              {participant.active ? (<>
                  <DialogField label="Reason" required>
                    <Select onValueChange={(value) => {
                setStatusDraft((current) => ({
                    ...current,
                    reason: value,
                }));
            }} value={statusDraft.reason}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select reason"/>
                      </SelectTrigger>
                      <SelectContent>
                        {DEACTIVATION_OPTIONS.map((reason) => (<SelectItem key={`deactivation-${reason}`} value={reason}>
                            {reason}
                          </SelectItem>))}
                      </SelectContent>
                    </Select>
                  </DialogField>
                  {statusDraft.reason === 'Other' ? (<DialogField label="Other Reason" required>
                      <Input onChange={(event) => {
                    setStatusDraft((current) => ({
                        ...current,
                        otherReason: event.target.value,
                    }));
                }} value={statusDraft.otherReason}/>
                    </DialogField>) : null}
                  <DialogField label="Deactivation Date" required>
                    <Input max={maxDeactivationDate} min={minDeactivationDate} onChange={(event) => {
                setStatusDraft((current) => ({
                    ...current,
                    activationBackDate: event.target.value,
                }));
            }} type="date" value={statusDraft.activationBackDate}/>
                  </DialogField>
                </>) : (<div className="rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-30)] bg-[var(--surface-muted)] px-4 py-3 text-sm text-[var(--neutral-gray-120)]">
                  This will mark the client active again.
                </div>)}
            </div>
            <DialogFooter className="border-t border-[var(--neutral-gray-40)] px-5 py-4">
              <Button disabled={pendingAction !== null} onClick={closeDialog} type="button" variant="outline">
                Cancel
              </Button>
              <Button disabled={pendingAction !== null} type="submit">
                {pendingAction === 'status'
            ? 'Saving...'
            : participant.active
                ? 'Deactivate Client'
                : 'Activate Client'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>);
};
